import { useCallback, useEffect, useMemo, useRef, useState, type Ref, type SetStateAction } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApprovalState,
  ApprovalType,
  campaignPostReopenFrom,
  canReleasePost,
  ContentChannel,
  ContentKind,
  contentEditableReason,
  scheduleLockReason,
  socialCharLimit,
  socialImageLimit,
  SocialPlatform,
  BLOG_EXPORT_DESTINATION,
  BLOG_EXPORT_FORMATS,
  DEFAULT_BLOG_EXPORT_FORMAT,
  isDocumentMime,
  isPostOverdue,
  neverSayWarning,
  THREAD_PLATFORM_MESSAGE,
} from '@mg/shared';
import type {
  Campaign,
  ApplyScheduleToSetDto,
  ApplyMediaToSetResult,
  ApplyScheduleToSetResult,
  EnqueuePostDto,
  GeneratedContent,
  GenerateContentDto,
  Post,
  SocialConnection,
  BlogExportFormat,
} from '@mg/shared';
import {
  CalendarClock,
  CalendarDays,
  ChevronRight,
  Clock,
  Construction,
  Lock,
  Shapes,
  Send,
  ShieldCheck,
  Sparkles,
  X,
  Zap,
  type LucideIcon,
  Download,
} from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { LostPublishError, sendPublish, useLostPublishWatch } from '../../lib/lostRequest';
import { hcUnderlineIdle, hcUnderlineSelected } from '../../lib/forcedColors';
import { useTenantKey } from '../../lib/useTenantKey';
import { useTenant } from '../../auth/TenantContext';
import { useEffectiveApprovalType } from '../../lib/useEffectiveApprovalType';
import { GenerateDesignModal } from '../../features/design/GenerateDesignModal';
import { GenerateMediaModal } from '../../features/media/GenerateMediaModal';
import { attachToPost } from '../../features/composer/attachToPost';
import type { PostContext, SaveToPost } from '../../features/post-context/postContext';
import { MarkPublishedModal } from '../../features/posts/MarkPublishedModal';
import { enumLabel, formatDateTime, formatDateTimeLong, formatRelative } from '../../lib/display';
import { cn } from '../../lib/cn';
import { AlertBanner } from '../../components/ui/AlertBanner';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FormField, Input, Select } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';
import { downloadPostExport } from '../../features/media/mediaDisplay';
import { DestinationTabs } from '../../features/composer/DestinationTabs';
import { PlatformIcon } from '../../features/posts/PlatformIcon';
import { ErrorState, LoadingState } from '../../components/ui/States';
import { useDialogs } from '../../components/ui/DialogProvider';
import { NON_SOCIAL_PLATFORMS } from '../../features/posts/postTypes';
import { ContentBlocksEditor } from '../../features/composer/ContentBlocksEditor';
import { copiedTextFrom, forgetCopiedText } from '../../features/composer/copiedText';
import { IconButton } from '../../features/composer/EditorControls';
import { MediaLibraryPanel } from '../../features/composer/MediaLibraryPanel';
import { PostApprovalPanel } from '../../features/composer/PostApprovalPanel';
import { SchedulePickerCalendar } from '../../features/composer/SchedulePickerCalendar';
import {
  categoryForSave,
  GENERAL,
  SchedulingCategoryField,
  useSchedulingCategories,
} from '../../features/composer/SchedulingCategoryField';
import {
  isInPast,
  nextQuarterHour,
  QUARTER_HOUR_STEP_SECONDS,
} from '../../features/composer/quarterHour';
import {
  attachDocument,
  EMPTY_PREVIEW_MESSAGE,
  persistable,
  toUpdateContentDto,
  newImageBlock,
  newTextBlock,
  toCreateContentDto,
  type EditorBlock,
} from '../../features/composer/blocks';
import {
  adoptContentIds,
  aiTargetKey,
  anyPostLocked,
  applyGeneratedText,
  applyThreadLocks,
  composerGates,
  contentSavePlan,
  createContentsFromPlan,
  ensureSocialText,
  formFromSavedPost,
  insertMediaAtAnchor,
  isPublishBlocked,
  isPublishNowHeld,
  isSaveBlocked,
  isThreadForm,
  mediaSlotFor,
  persistRefusal,
  publishHoldReason,
  savedPostBlocks,
  threadAccountAllowed,
  threadFinishConfirmText,
  threadSavePlan,
  threadSummaryLine,
  threadSwitchBlock,
} from '../../features/composer/thread';
import { ThreadUnconfirmedNotice } from '../../features/composer/ThreadUnconfirmedNotice';
import { ThreadConvertBanner } from '../../features/composer/ThreadConvertBanner';
import { ThreadConvertCard } from '../../features/composer/ThreadConvertCard';
import { useThreadConversion } from '../../features/composer/useThreadConversion';
import { TestModeBanner } from '../../features/testMode/TestModeBanner';
import { isUploading, useUploadCounts } from '../../features/composer/uploadHold';
import { useComposerPaneHeight } from '../../features/composer/useComposerPaneHeight';
import { previewModel } from '../../features/composer/previewModel';
import { PreviewAssistCard } from '../../features/composer/PreviewAssistCard';
import { PostPerformance } from '../../features/composer/PostPerformance';
import { PlatformPreview } from '../../features/social/PlatformPreview';
import { ThreadPreview } from '../../features/social/ThreadPreview';
import { threadPreviewPosts } from '../../features/social/threadPreviewPosts';
import { isPreviewPlatform } from '../../features/social/previewRules';
import { reconnectNeeded, reconnectSignInName } from '../../features/social/reconnect';
import { platformName } from '../../features/social/platforms';
import { useDebounced } from '../../lib/useDebounced';
import { useProxyImage, useThumbnailSrc } from '../../lib/useProxyImage';

/**
 * DISPLAY order of the content-type tabs — Social first, because it is by far the most-used
 * channel; the rest follow in their enum order. Deliberately NOT the `ContentChannel` enum's own
 * order: that enum is mirrored as a Postgres enum in the Prisma schema, so reordering it to suit
 * the UI would be a database change (and would silently re-order every other consumer, e.g. the
 * approver matrix columns). Keep this list exhaustive — a channel missing here loses its tab.
 */
const CHANNEL_TAB_ORDER: ContentChannel[] = [
  ContentChannel.SOCIAL,
  ContentChannel.BLOG,
  ContentChannel.VIDEO,
];

/**
 * PODCAST is not offered — distributing one needs feed hosting we aren't taking on yet — but it is
 * NOT removed from the enum, because posts already exist with it. Those still open, still show what
 * they are, and still save: the picker adds a disabled Podcast row when, and only when, the post it
 * is editing is one. Dropping the option outright would render an unlabelled control on those posts,
 * and clicking any option would silently convert them.
 */
const RETIRED_CHANNELS: ContentChannel[] = [ContentChannel.PODCAST];

/**
 * The "When to post" row a stored schedule route belongs to.
 *
 * EXACT covers both the date picker and a suggested slot — they are the same write, and a slot
 * highlights itself by matching the instant, so the picker row is the right home for the rest.
 */
function whenModeForRoute(
  route: Post['scheduleRoute'],
): 'next' | 'prioritize' | 'date' | 'month' | null {
  switch (route) {
    case 'NEXT':
      return 'next';
    case 'PRIORITIZE':
      return 'prioritize';
    case 'MONTH':
      return 'month';
    case 'EXACT':
      return 'date';
    default:
      return null;
  }
}

/** Format an ISO timestamp for a <input type="datetime-local"> value (local time, no seconds). */
function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

/** Destination options for a non-social channel (Blog/Video/Podcast); [] for social. */
function nonSocialOptions(channel: ContentChannel): string[] {
  if (channel === ContentChannel.SOCIAL) return [];
  return NON_SOCIAL_PLATFORMS[channel as 'BLOG' | 'VIDEO' | 'PODCAST'] ?? [];
}

/**
 * What applying to the set actually did.
 *
 * Green, and the words the design asks for, when every sibling took the settings. When one could
 * not — already published, locked in review — it says so instead: "applied to all posts in set"
 * over a post that did not move is the kind of confident lie someone only discovers on the day it
 * was supposed to go out.
 */
function SetApplyResult({ result }: { result: ApplyScheduleToSetResult }) {
  const applied = result.outcomes.filter((o) => o.applied);
  const skipped = result.outcomes.filter((o) => !o.applied);

  if (skipped.length === 0) {
    return <span className="text-sm font-medium text-success dark:text-success-dark">Applied to all posts in set</span>;
  }
  const name = (o: (typeof skipped)[number]) =>
    o.platform ? platformName(o.platform) : (o.accountName ?? 'One destination');
  return (
    <span className="text-sm text-amber-700 dark:text-warning-dark">
      Applied to {applied.length} of {result.outcomes.length}.{' '}
      {skipped.map((o) => `${name(o)}: ${o.reason ?? 'could not be scheduled'}`).join(' ')}
    </span>
  );
}

/**
 * Why the queue and the Suggested Slots have no time to give (`scheduleEmpty`), naming the schedule:
 *  - a post with a category: a category with no times uses General's, so both have none;
 *  - a post on General: General has none, and choosing a category is the other way out - offered
 *    only when the workspace has one.
 * The same words the API refuses a queue placement with.
 */
function NoPostingTimes({
  categoryName,
  offerCategory,
  onOpenSettings,
}: {
  /** The category the post is saved with; null = General. */
  categoryName: string | null;
  /** The workspace has a scheduling category the post could be given. */
  offerCategory: boolean;
  onOpenSettings: () => void;
}) {
  const settings = (
    <button
      type="button"
      onClick={onOpenSettings}
      className="font-medium text-link hover:text-action-hover"
    >
      Scheduler Settings
    </button>
  );
  return categoryName !== null ? (
    <>
      The &quot;{categoryName}&quot; category has no posting times, and General has none either. Add
      posting times in {settings}.
    </>
  ) : (
    <>
      General has no posting times. Add some in {settings}
      {offerCategory && ', or choose a scheduling category for this post'}.
    </>
  );
}

/** One row in the composer's "When to post" list (icon · title · description · chevron/badge). */
function ScheduleOptionRow({
  icon: Icon,
  title,
  description,
  onClick,
  disabled,
  chevron,
  active,
  selected,
  badge,
  buttonRef,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  chevron?: boolean;
  active?: boolean;
  /** This is the timing the post is on — filled, so it reads as chosen rather than merely open. */
  selected?: boolean;
  badge?: string;
  /** The row's button, for a caller that moves keyboard focus to it. */
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'focus-ring flex w-full items-start gap-3 px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60',
        // The action fill, every child in its foreground colour — an unselected row keeps its own
        // hover, which on a filled row would wash the selection out.
        selected ? 'bg-action text-action-fg' : cn('hover:bg-hover', active && 'bg-hover'),
      )}
    >
      <Icon
        className={cn('mt-0.5 h-5 w-5 shrink-0', selected ? 'text-action-fg' : 'text-ink-faint')}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className={cn('block text-base font-semibold', selected ? 'text-action-fg' : 'text-ink')}>
          {title}
        </span>
        <span className={cn('block text-sm', selected ? 'text-action-fg/90' : 'text-ink-muted')}>
          {description}
        </span>
      </span>
      {badge && (
        <span
          className={cn(
            'mt-0.5 rounded-full px-2 py-0.5 text-xs font-semibold',
            selected ? 'bg-action-fg/20 text-action-fg' : 'bg-chip text-ink-label',
          )}
        >
          {badge}
        </span>
      )}
      {chevron && (
        <ChevronRight
          className={cn(
            'mt-1 h-5 w-5 shrink-0',
            selected ? 'text-action-fg' : 'text-ink-ghost',
            active && 'rotate-90',
          )}
          aria-hidden
        />
      )}
    </button>
  );
}

export interface ContentComposerProps {
  /** When set, loads and edits that Post; otherwise starts a fresh post. */
  id?: string;
  /**
   * Called after a successful save/publish or on Cancel. When provided (e.g. the
   * site-wide ComposerModal), it replaces the fallback navigate('/posts').
   */
  onClose?: () => void;
  /**
   * Open a sibling destination of this post. Provided by the modal, which switches destination by
   * re-pointing `?compose=` — the same transition as opening any other post.
   */
  onSelectSibling?: (postId: string) => void;
}

/**
 * Platforms we can natively "Publish now" as text. Instagram and YouTube Shorts
 * require media, so they can still be a post's destination but the Publish button
 * is disabled for them (the API rejects text-only posts to those).
 */
const PUBLISHABLE_PLATFORMS = new Set<SocialPlatform>([
  SocialPlatform.LINKEDIN,
  SocialPlatform.FACEBOOK,
  SocialPlatform.X,
  SocialPlatform.THREADS,
]);

/**
 * Platforms that publish from an attached image rather than text — the caption is
 * optional, but an image piece is required. The backend reads the image from the
 * saved post, so nothing extra is passed from the client.
 */
const IMAGE_PUBLISH_PLATFORMS = new Set<SocialPlatform>([SocialPlatform.INSTAGRAM]);

/** The title a post is saved with when the field is left blank. Internal: it is never published. */
const UNTITLED_POST = 'Untitled post';

/**
 * The Post composer. A Post belongs to a Campaign (the approval/release owner) and
 * owns an ordered list of Content pieces (TEXT + media). Loads an existing post by
 * `id`, or starts a new one (optionally preset to a campaign via `?campaign=`).
 * Save persists the post + its contents; the AI assist create-then-generates and
 * applies the result to the focused TEXT piece. Editing is blocked (read-only) when
 * the owning campaign is past DRAFT/REVISE — the server enforces the same gate.
 */
export function ContentComposer({ id, onClose, onSelectSibling }: ContentComposerProps) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tenantKey = useTenantKey();
  const { activeTenant } = useTenant();
  // EFFECTIVE approval mode (global override folds in) — must match the server so one-off gating
  // and schedule/publish gating never diverge from what the API enforces.
  const approvalType = useEffectiveApprovalType();
  const queryClient = useQueryClient();
  const { confirm } = useDialogs();

  const isEditing = Boolean(id);
  const lockedCampaignId = params.get('campaign');
  /** Campaign is fixed (not choosable) when editing a post or preset via `?campaign=`. */
  const campaignLocked = isEditing || Boolean(lockedCampaignId);

  const [campaignId, setCampaignId] = useState<string>(lockedCampaignId ?? '');
  const [creatingNewCampaign, setCreatingNewCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  /** "No campaign — one-off post": the server clusters it into the "One-Off Posts" bucket. */
  const [oneOff, setOneOff] = useState(false);
  /**
   * The "Scheduling category" field's value: the category whose times the queue uses for this post.
   * GENERAL, a category's id, or '' - nothing chosen yet, a new post's start. A save treats '' as
   * General (categoryForSave), as the server does a post without a category.
   */
  const [categoryId, setCategoryId] = useState('');

  // Default for a NEW post — matches the first tab (Social), the most-used channel. An EDITED post
  // overrides this from the saved row during hydration, so its own channel is always respected.
  const [channel, setChannel] = useState<ContentChannel>(ContentChannel.SOCIAL);
  const [platform, setPlatform] = useState<SocialPlatform>(SocialPlatform.LINKEDIN);
  /**
   * The platform the CURRENT text was written for — the baseline the rewrite notice compares
   * against. Not the same as "the saved platform": on a new post it tracks whatever is selected
   * until text exists, so choosing a platform before writing is not a "change".
   */
  const [textPlatform, setTextPlatform] = useState<SocialPlatform | null | undefined>(undefined);
  const [platformLabel, setPlatformLabel] = useState<string>('');
  const [exportFormat, setExportFormat] = useState<BlogExportFormat>(DEFAULT_BLOG_EXPORT_FORMAT);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [title, setTitle] = useState(params.get('title') ?? '');

  const [blocks, setBlocks] = useState<EditorBlock[]>(() => [newTextBlock()]);
  /** A publish is in flight - set as it starts, cleared once it and its read-back are over. */
  const sendingRef = useRef(false);
  /**
   * How the editor and the Media Library change the form. While a thread is being sent nothing
   * enters it: every text box and media box of it is disabled then, so a change that still arrives
   * is a late one - an upload started before "Publish now", or an insert from the library - and it
   * would sit in a post that may come back live, and be refused on every save after. Read from a
   * ref when the change lands, not from a render: an upload can land after the editor was unmounted
   * (the Schedule tab's "Publish Now"). The composer's own changes - the ids a save adopts, the
   * locks the read-back brings - do not come this way.
   */
  const setEditorBlocks = useCallback(
    (action: SetStateAction<EditorBlock[]>) =>
      setBlocks((prev) => {
        if (sendingRef.current && isThreadForm(prev)) return prev;
        return typeof action === 'function' ? action(prev) : action;
      }),
    [],
  );
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  /**
   * The text box that last had focus. A thread's AI assist writes there: "Add media" in a thread
   * post moves `focusedKey` to a media block, and that must not send a draft to another post.
   */
  const [focusedTextKey, setFocusedTextKey] = useState<string | null>(null);
  const [savedPostId, setSavedPostId] = useState<string | null>(id ?? null);
  /** Set once this session has scheduled the post — see applyScheduleResult. */
  const [scheduled, setScheduled] = useState(false);
  /** Server Content ids currently persisted on the post (for the edit diff). */
  const [originalContentIds, setOriginalContentIds] = useState<string[]>([]);
  /**
   * The SAVED post is a thread, or part of it is live: read off the post as loaded and off the post
   * every save returns. It decides how the next save writes the content (contentSavePlan) - the
   * server refuses such a post on the row-by-row endpoints.
   */
  const [storedThread, setStoredThread] = useState(false);
  /**
   * How many persist() calls are running. A count, not a flag: two can overlap ("Save changes"
   * beside "Save and close"), and the first to finish must not say that nothing is being saved.
   */
  const [persistCount, setPersistCount] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * Never say phrases still in the last AI Assist draft after the server's one regeneration (Brand
   * Workspace v2 only: the response carries `reviewTerms` only while the install's flag is on). Shown as
   * one amber line under the AI Assist buttons; cleared when the next generation starts.
   */
  const [neverSayTerms, setNeverSayTerms] = useState<string[]>([]);
  /**
   * When set, the Media Library replaces the AI assist sidebar. Identifies the gallery that asked
   * by key rather than by a captured inserter, so the insert resolves against the block list as it
   * is at Insert time — the post stays editable behind the panel.
   */
  /**
   * The Media Library open in the sidebar slot, and for what: images and video for the gallery
   * anchored at `anchorKey`, or ONE PDF for the PDF panel `anchorKey`.
   */
  /**
   * Files on their way into the post - photos, videos, a PDF, an imported design - counted from each
   * upload's own start and end (uploadHold.ts). Kept here, not in the editor: the editor is drawn on
   * the Content tab only, and an upload started there still lands while Schedule is open.
   */
  const uploads = useUploadCounts();
  const uploading = isUploading(uploads.counts);
  /**
   * The Content tab's grid, as an element held in state - not a ref object: the grid is drawn on
   * the Content tab only, and the measurement has to start again each time it is drawn.
   */
  const [paneRow, setPaneRow] = useState<HTMLDivElement | null>(null);
  useComposerPaneHeight(paneRow);
  const [mediaPicker, setMediaPicker] = useState<{
    anchorKey: string;
    mode: 'gallery' | 'document';
  } | null>(null);
  /**
   * Generate media or Generate design, opened from this post. `anchorKey` is the gallery Generate
   * media was opened from, a key for the same reason as the Media Library's; Generate design has
   * none, and its image joins the post's first gallery.
   */
  const [generateFor, setGenerateFor] = useState<{
    kind: 'media' | 'design';
    anchorKey: string | null;
  } | null>(null);
  /** Said under the post after "Save and return to post", e.g. that a PNG copy used a stand-in font. */
  const [mediaNotice, setMediaNotice] = useState<string | null>(null);
  // Tabs (only when editing a saved post): Content (fields) · Schedule · Approval.
  // Open straight to a tab when the caller asks (e.g. a post card's "Schedule" → `&ctab=schedule`).
  // Uses `ctab`, NOT `tab`, to avoid colliding with the Campaigns/Posts hub's Status filter.
  const initialTab = params.get('ctab');
  const [activeTab, setActiveTab] = useState<'content' | 'schedule' | 'approval'>(
    initialTab === 'schedule' || initialTab === 'approval' ? initialTab : 'content',
  );
  const [scheduleAt, setScheduleAt] = useState('');
  /**
   * The timing option this post is on, so the chosen row can read as chosen.
   *
   * Seeded from the post itself (`scheduleRoute`), which is why it survives closing and reopening
   * the modal. It has to be stored rather than inferred: a queued post looks identical whether it
   * got there by Next Available, Prioritize or a month, and `queued` alone cannot tell them apart.
   * Posts scheduled before that was recorded have no route and show nothing selected — an honest
   * blank rather than a guess.
   */
  const [chosenWhen, setChosenWhen] = useState<'next' | 'prioritize' | 'date' | 'month' | null>(null);

  /** The decision to offer the set, and what happened when it was taken. */
  const [setDecision, setSetDecision] = useState<ApplyScheduleToSetDto | null>(null);
  const [setResult, setSetResult] = useState<ApplyScheduleToSetResult | null>(null);

  // Which "When to post" option is expanded inline (Set Date & Time / Pick a Month).
  const [whenMode, setWhenMode] = useState<'date' | 'month' | null>(null);
  const [monthValue, setMonthValue] = useState('');

  const campaignFieldId = useFieldId('composer-campaign');
  const newCampaignFieldId = useFieldId('composer-new-campaign');
  const titleId = useFieldId('composer-title');
  const platformFieldId = useFieldId('composer-platform');
  const reconnectNoteId = useFieldId('composer-reconnect-note');
  const exportFormatFieldId = useFieldId('composer-export-format');

  // Load the post once it exists — either opened for editing (`id`) or created during this session
  // (`savedPostId`) — so a brand-new post's Schedule/Approval tabs become live the moment it's saved.
  const effectiveId = id ?? savedPostId;
  const existing = useQuery({
    queryKey: ['post', tenantKey, effectiveId],
    queryFn: ({ signal }) => api.getPost(effectiveId as string, signal),
    enabled: Boolean(effectiveId) && tenantKey !== '',
  });

  const campaignsQ = useQuery({
    queryKey: ['campaigns', tenantKey],
    queryFn: ({ signal }) => api.listCampaigns(signal),
    enabled: tenantKey !== '',
  });
  /**
   * Only campaigns that can still take a post — i.e. those still in Draft.
   *
   * Once a campaign has been sent for approval or published, quietly attaching a new post to it
   * changes something already agreed. Offering those in the picker made that a one-click accident, so
   * they are no longer listed.
   *
   * The "One-Off Posts" bucket is not offered either. It is a real campaign row, so it turned up in
   * this list looking like any other choice — but picking it is not how a post becomes a one-off
   * (the "No campaign" button is), and it walked straight past the rule that Campaign-level
   * approvals forbid one-offs: the server only refuses a MISSING campaign, so naming the bucket
   * explicitly was accepted.
   *
   * The campaign this post is ALREADY in is kept regardless of either rule. Filtering it out would
   * leave the Select with a value that matches no option, which browsers render as blank — so an
   * edit that touched nothing about the campaign would silently reassign the post on save.
   */
  const campaigns = useMemo(() => {
    const all = campaignsQ.data ?? [];
    return all.filter(
      (c) =>
        c.id === campaignId ||
        (c.approvalState === ApprovalState.DRAFT && !c.isOneOff),
    );
  }, [campaignsQ.data, campaignId]);

  const connectionsQ = useQuery({
    queryKey: ['social-connections', tenantKey],
    queryFn: ({ signal }) => api.listSocialConnections(signal),
    enabled: tenantKey !== '',
    // The reconnect note under Platform sends the person to Connections in a new tab. Back on this
    // tab, the account they reconnected there must read as healthy without reopening the post: read
    // the list again each time the tab is shown. 'always', not true - the app's 30 s staleTime
    // would skip a reconnect that came back quickly.
    refetchOnWindowFocus: 'always',
  });
  // The "Scheduling category" field's options; a save sends only a category the field can show.
  const categoriesQ = useSchedulingCategories();
  const accounts = useMemo(
    () => (connectionsQ.data ?? []).filter((c): c is SocialConnection => c.connected && Boolean(c.id)),
    [connectionsQ.data],
  );

  // Hydrate the form from a loaded post — ONCE per post id. Guarding with a ref means a later
  // cache update to this post (e.g. an approval action on the Approval tab calls setQueryData so
  // the Schedule/Content gating refreshes) does NOT re-run hydration and clobber unsaved edits.
  const hydratedIdRef = useRef<string | null>(null);
  // The same, as state, for what renders from the form: the post preview waits for it.
  const [hydratedId, setHydratedId] = useState<string | null>(null);
  /**
   * What the "Scheduling category" field shows.
   *  - An opened post whose form is not filled from it yet shows what it is SAVED with (none =
   *    General). The form is drawn in the render the post arrives in, and filled by the effect below
   *    a render later: read from the form, that first frame painted the placeholder over the post's
   *    real category - on every open of an edited post whose category list was already in.
   *  - Nothing chosen yet, in a workspace with no categories, is General: there is nothing else the
   *    post could be on. Read off the list in the render it arrives in - not set by an effect a render
   *    later - so the placeholder never shows first. With categories, nothing chosen stays the
   *    placeholder until someone chooses.
   */
  const categoryFieldValue =
    id && hydratedId !== id && existing.data
      ? (existing.data.categoryId ?? GENERAL)
      : categoryId === '' && categoriesQ.data?.length === 0
        ? GENERAL
        : categoryId;
  useEffect(() => {
    const post = existing.data;
    if (!post || hydratedIdRef.current === post.id) return;
    hydratedIdRef.current = post.id;
    setHydratedId(post.id);
    setTitle(post.title);
    setChannel(post.channel);
    if (post.platform) setPlatform(post.platform);
    if (post.platformLabel) setPlatformLabel(post.platformLabel);
    if (post.exportFormat) setExportFormat(post.exportFormat);
    if (post.socialConnectionId) setConnectionId(post.socialConnectionId);
    setCampaignId(post.campaignId);
    // What the post is saved with: a post without a category is on General, and the field says so.
    setCategoryId(post.categoryId ?? GENERAL);
    setSavedPostId(post.id);
    setScheduleAt(post.scheduledAt ? toDatetimeLocalValue(post.scheduledAt) : '');
    setChosenWhen(whenModeForRoute(post.scheduleRoute));
    const sorted = [...(post.contents ?? [])].sort((a, b) => a.order - b.order);
    // A social post that is a thread opens as its posts, each with what is live locked; any other
    // post as it always has - and a social post always with a text box, since "Add text" is gone
    // (formFromSavedPost, which is handed the empty box to use).
    setBlocks(formFromSavedPost(post, newTextBlock()));
    setOriginalContentIds(sorted.map((c) => c.id));
    setStoredThread(post.thread != null);
    // The text that just loaded was written for the post's SAVED platform - unless this browser tab
    // copied it here from another platform with "Add destination" and it has not been saved since
    // (copiedText.ts): then it was written for that one, and the new destination opens asking for a
    // rewrite. Set the baseline here rather than letting the sync effect below do it: hydration
    // arrives with text already present, so that effect has stopped syncing by then and the baseline
    // would be stuck on the composer's pre-hydration default — flagging a rewrite on every open of a
    // post that never changed.
    const copiedFrom =
      post.channel === ContentChannel.SOCIAL ? copiedTextFrom(tenantKey, post.id) : null;
    setTextPlatform(
      copiedFrom ?? post.platform ?? (post.channel === ContentChannel.SOCIAL ? undefined : null),
    );
  }, [existing.data, tenantKey]);

  // Locks stay true to the saved post. Which posts of a thread are live, or waiting for a decision,
  // changes without the form being filled again: a publish that stopped part-way, a decision about
  // a post, another save. So every answer that lands in the post cache is read for them - by ROW,
  // never by position (applyThreadLocks). A post answered without its `thread` is "not loaded",
  // not "not a thread", and changes nothing; neither does a form that is not a thread.
  useEffect(() => {
    const post = existing.data;
    if (post) setBlocks((prev) => applyThreadLocks(prev, post));
  }, [existing.data]);

  // A fresh post opens with NO campaign chosen. It used to auto-select the first editable one,
  // which quietly decided where someone's post was going — and the decision was invisible unless
  // they thought to check the field. Blank and required makes it a choice; `persist()` already
  // refuses to save without one ("Choose a campaign for this post.").

  // Default the social destination once accounts load (pick a platform-matching account, else the
  // first connected one). NEVER auto-reassign a post that ALREADY has a bound account: silently
  // moving it to a different one IS the wrong-account bug, so if its bound account is disconnected
  // we leave connectionId as-is and the user explicitly re-picks before publishing (the account
  // Select stays enabled while releasable, and now shows "Previous account unavailable"). A post
  // that never had a binding has nothing to protect, so it defaults exactly like a new one.
  //
  // A thread is never defaulted onto an account the picker would not offer it: one whose platform
  // takes no thread or, once part of the thread is live, any platform but the thread's own. Its
  // account going away must not move it there - every save after that would be refused.
  const firstAccountFits =
    accounts.length > 0 &&
    threadAccountAllowed(blocks, accounts[0].platform, existing.data?.platform ?? null);
  useEffect(() => {
    if (channel !== ContentChannel.SOCIAL || accounts.length === 0) return;
    if (connectionId && accounts.some((a) => a.id === connectionId)) return;
    // A SAVED post is never given an account by default - not even one that has none. A default
    // only ever lives in the picker: the post looked set to an account the server had never heard
    // of, so "Retry" refused with "choose which account" under a picker showing one chosen. Its
    // picker says "Choose an account…" until someone does.
    if (isEditing) return;
    const match = accounts.find((a) => a.platform === platform);
    if (match) {
      setConnectionId(match.id ?? null);
      return;
    }
    // No account for this platform. A SAVED post keeps its own platform — auto-moving it is a
    // destination change, which now DROPS the post's schedule the moment the user saves anything
    // (posts.service.update). So leave it alone and let the Select say why nothing is picked; only
    // a brand-new post, which has no destination to lose, falls back to the first account.
    if (isEditing && existing.data?.platform) return;
    if (!firstAccountFits) return;
    setConnectionId(accounts[0].id ?? null);
    setPlatform(accounts[0].platform);
    // The COMPOSER picked this destination, not the user. Anything already typed was written
    // against the initial placeholder — which the Select never even offered — so move the rewrite
    // baseline with it. Without this, typing while connections are still loading raises "you have
    // changed posting platforms" for a change the app made.
    setTextPlatform(accounts[0].platform);
  }, [
    accounts,
    channel,
    connectionId,
    platform,
    isEditing,
    existing.isLoading,
    existing.data?.socialConnectionId,
    existing.data?.platform,
    firstAccountFits,
  ]);

  // Default the destination for a non-social channel to a valid option.
  useEffect(() => {
    const opts = nonSocialOptions(channel);
    if (opts.length === 0) return;
    // Default an EMPTY destination, and re-default one that belongs to a different channel (the
    // channel just changed). Anything else is left alone.
    //
    // This used to reset any label not in the list, which silently rewrote a destination the list
    // does not happen to contain — a legacy or hand-set value — every time the post was opened. That
    // was survivable while the fallback was WordPress; with Export first it would quietly convert
    // such a post into an export, which is exactly what "existing posts keep their destination"
    // rules out.
    const fromAnotherChannel = Object.entries(NON_SOCIAL_PLATFORMS).some(
      ([ch, list]) => ch !== channel && list.includes(platformLabel),
    );
    if (!platformLabel || fromAnotherChannel) setPlatformLabel(opts[0]);
  }, [channel, platformLabel]);

  /**
   * Why "Add destination" is not available yet, or null.
   *
   * A destination is a sibling POST, seeded by copying this one — so this post has to be saved
   * first, and it cannot be saved without somewhere to live. Blocking the button is kinder than
   * letting it save into a campaign the user has not chosen yet; `persist()` refuses that anyway
   * ("Choose a campaign for this post."), it just does so after the click.
   */
  const hasCampaignHome =
    isEditing ||
    campaignLocked ||
    oneOff ||
    Boolean(campaignId) ||
    (creatingNewCampaign && newCampaignName.trim().length > 0);
  const addDestinationBlockedReason: string | null = hasCampaignHome
    ? null
    : 'Please select a campaign before adding additional destinations.';

  const resolvedCampaignId = isEditing
    ? (existing.data?.campaignId ?? null)
    : oneOff || creatingNewCampaign
      ? null
      : campaignId || null;

  // One-off posts are only allowed in Per-Post / None modes. In Campaign mode we still show the
  // option (so the rule is discoverable) but block Save with a warning — the server enforces too.
  const oneOffBlocked = !isEditing && oneOff && approvalType === ApprovalType.CAMPAIGN;

  const selectedCampaign: Campaign | null =
    campaigns.find((c) => c.id === resolvedCampaignId) ?? null;
  const campaignState: ApprovalState | null =
    existing.data?.campaignApprovalState ?? selectedCampaign?.approvalState ?? null;

  // The state that governs this post: its OWN where it has one - in CAMPAIGN mode that is its lane's,
  // which is what the server reads - else its campaign's. (NOTE: campaignApprovalState is absent on
  // the post DETAIL DTO; campaignState comes from the campaigns list.) Shown in the lock banner.
  const ownState: ApprovalState | null = isEditing ? (existing.data?.approvalState ?? null) : null;
  const gateState: ApprovalState | null =
    approvalType === ApprovalType.POST
      ? isEditing
        ? (ownState ?? ApprovalState.DRAFT)
        : null
      : (ownState ?? campaignState);

  // Editable exactly when the server takes the save: contentEditableReason is the server's own rule
  // (shared). Reading the campaign alone locked a failed post that its lane had reopened - a campaign
  // stays PUBLISHED once its destinations have gone out or failed. A brand-new campaign, or a state
  // not known yet, is editable. A post that has ALREADY GONE OUT is read-only whatever the mode says:
  // a save would make our record disagree with what is live, and it can't be re-released anyway.
  const readOnly =
    existing.data?.publishedAt != null ||
    (!creatingNewCampaign &&
      gateState != null &&
      contentEditableReason(approvalType, ownState, campaignState ?? gateState) != null);

  // Mirror the server's publishNow() release gate (posts.service.ts) so the "Publish
  // now" button only shows when the API would accept it — the editable (!readOnly)
  // window is DISJOINT from the releasable one, so gating on !readOnly both 400s and
  // hides the button exactly when it should appear. Never for an already-published
  // post; CAMPAIGN gates on the owning campaign, POST on the post's own state, NONE has
  // no gate. A brand-new unsaved post has no state yet (treated as DRAFT), so it's
  // releasable only in NONE mode — in CAMPAIGN/POST you approve before publishing.
  // Release gate — mirrors posts.service.ts publish()/publishNow(). NOTE: scheduledAt is NOT a
  // releasable signal anymore — a post can carry a PROPOSED schedule while still DRAFT, so release
  // is gated on the APPROVED/SCHEDULED state only (NONE mode always releasable once saved).
  const postState: ApprovalState = existing.data?.approvalState ?? ApprovalState.DRAFT;
  // THE SAME FUNCTION the server's publish()/publishNow() gate on (canReleasePost) - including the
  // retry of a failed post, which the hand-written copy here missed: a failed post in a released
  // campaign had no Publish now and a disabled account picker, so its account could not be fixed.
  const releasable = canReleasePost(
    existing.data ?? {},
    campaignState ?? ApprovalState.DRAFT,
    approvalType,
  );

  // CAMPAIGN mode: a post past review - approved, scheduled, or failed - can be reopened on its own
  // ("Reopen this post"), by the server's own rule; one under review goes back with its lane.
  const ownStateReopenable = campaignPostReopenFrom(existing.data ?? {}) != null;

  // A PROPOSED schedule can be set while still editable (before submit) OR after approval (release);
  // mid-review it's locked - the server's own rule (scheduleLockReason, shared), on the post's own
  // state first. Unknown until the post's or its campaign's state has loaded. The Schedule tab is
  // edit-only, so savedPostId is always present here.
  const canSchedule =
    Boolean(savedPostId) &&
    !existing.data?.publishedAt &&
    (approvalType !== ApprovalType.CAMPAIGN || gateState != null) &&
    scheduleLockReason(approvalType, ownState, campaignState ?? gateState ?? ApprovalState.DRAFT) ===
      null;
  // "Post as soon as approved" is a PRE-submit choice (POST mode, editable post only) — mirrors the
  // server gate (DRAFT/REVISE). It and a fixed time are mutually exclusive.
  const canSetPublishWhenApproved =
    approvalType === ApprovalType.POST &&
    Boolean(savedPostId) &&
    (postState === ApprovalState.DRAFT || postState === ApprovalState.REVISE);

  // Scheduling is PER PLATFORM: a slot won on one platform's timeline can't carry over to another,
  // so saving a platform change makes the server DROP the schedule (and stamp the removed date on
  // `rescheduleRequiredFrom`). Warn BEFORE the save, while the choice is still reversible.
  //
  // Keyed on the PLATFORM, never on the bound account: a platform can have several connected
  // accounts, and switching between two Instagram accounts stays on Instagram's timeline — the slot
  // is still valid. The account <Select>'s onChange calls setPlatform(acct.platform), so an
  // account-only change re-sets `platform` to the SAME value and this stays false.
  //
  // Compared as the EFFECTIVE destination, exactly as posts.service.update() resolves it: a
  // non-social channel carries NO platform. Switching a scheduled social post to Blog/Video/Podcast
  // is therefore a destination change too (INSTAGRAM → null) and drops the slot just the same —
  // and it must warn, even though the Platform field itself is gone by then.
  const savedScheduledAt = existing.data?.scheduledAt ?? null;
  const effectivePlatform = channel === ContentChannel.SOCIAL ? platform : null;
  const platformChangeDropsSchedule =
    existing.data != null &&
    savedScheduledAt != null &&
    effectivePlatform !== (existing.data.platform ?? null) &&
    // A published post can't be re-pointed at all (the server refuses), and its scheduledAt is
    // just the record of when it went out — there is no slot left to lose.
    !existing.data.publishedAt &&
    // Only when a save can actually happen. On a read-only (approved/released) post the account
    // picker deliberately stays enabled so you can re-point a disconnected account before
    // publishing — that never writes `platform`, so nothing is at risk and "if you save the post…"
    // would promise an action the user has no button for.
    !readOnly;

  // Text already written for one platform rarely fits the next: the platforms have different hard
  // limits (X 280, Threads 500, the rest effectively unbounded — DEFAULT_AI_LENGTH_GUIDELINES) and
  // different conventions. So once a post HAS text, changing platform prompts a rewrite.
  //
  // The baseline is the DESTINATION the text was written for, not the saved one, so the notice is
  // driven by what the user actually did: picking a platform before writing anything is a choice,
  // not a change, and there is nothing to rewrite. Kept in sync until text exists; frozen after,
  // until a save or a hydration re-baselines it. `undefined` means "no baseline established" (a
  // saved SOCIAL post with no platform at all) — distinct from `null`, which is a real baseline
  // meaning the text was written for a non-social destination.
  const hasText = blocks.some((b) => b.kind === ContentKind.TEXT && b.text.trim() !== '');
  useEffect(() => {
    // Hydration owns the baseline for a saved post. Skip until the form has been FILLED from it:
    // when the post is already in the react-query cache - a destination tab opened a second time, a
    // post reopened - BOTH effects fire in the same mount flush, and this one would write the values
    // of the render before hydration (no text yet, the LINKEDIN placeholder) over the baseline
    // hydration had just set, and flag a rewrite on a post nobody touched: every set the Content
    // Strategist wrote, on each tab opened again. Gated on the hydrated id as STATE - the post this
    // render was drawn from - never on the ref, which hydration sets earlier in that same flush and
    // so let this through; nor on the order the effects are declared in.
    if (id && hydratedId !== id) return;
    if (!hasText) setTextPlatform(channel === ContentChannel.SOCIAL ? platform : null);
  }, [id, hydratedId, hasText, platform, channel]);
  const platformChangeNeedsRewrite =
    // Only when the text is moving INTO a destination that caps length — that's what the copy
    // promises. Blog → X warns (blog-length text never fits 280); X → Blog has no limit to break.
    channel === ContentChannel.SOCIAL &&
    hasText &&
    textPlatform !== undefined &&
    platform !== textPlatform &&
    // Same reason the reschedule warning is gated: on a read-only post the account picker stays
    // enabled but there is no Save, so asking for a rewrite promises an action they can't take.
    !readOnly;
  /**
   * The rewrite notice was dismissed. It stays away until the platform changes again - any change,
   * back to a platform it was dismissed on before as well: that is a new change, and the words still
   * were not written for it.
   */
  const [rewriteDismissed, setRewriteDismissed] = useState(false);
  useEffect(() => setRewriteDismissed(false), [platform]);
  const showRewriteNotice = platformChangeNeedsRewrite && !rewriteDismissed;
  const dismissRewrite = () => {
    setRewriteDismissed(true);
    // A destination added by hand would otherwise ask again each time it is opened (copiedText.ts).
    if (savedPostId) forgetCopiedText(tenantKey, savedPostId);
  };

  // The other half of the same story, AFTER the save: the post comes back with no schedule and the
  // removed date recorded. Both are read off the LIVE query (existing.data) rather than the local
  // form state — `scheduleAt` still holds the old time, since hydration runs once per post id.
  // Never on a published post: "please reschedule" is unsatisfiable once a post has gone out.
  const rescheduleRequiredFrom = existing.data?.rescheduleRequiredFrom ?? null;
  const needsReschedule =
    rescheduleRequiredFrom != null && !existing.data?.scheduledAt && !existing.data?.publishedAt;

  // The text box the AI assist reads and writes (aiTargetKey): the block in focus when it is a text
  // box, else the first one - and in a thread the text box that last had focus, never one in a post
  // that is already live.
  const aiTarget = aiTargetKey(blocks, focusedKey, focusedTextKey);
  const focusedTextBlock = blocks.find((b) => b.key === aiTarget) ?? null;

  /** The editor says which block has focus; a text box is also kept as the last one that had it. */
  const focusBlock = (key: string) => {
    setFocusedKey(key);
    if (blocks.some((b) => b.key === key && b.kind === ContentKind.TEXT)) setFocusedTextKey(key);
  };

  const closeOrNavigate = () => {
    void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['calendar', tenantKey] });
    if (resolvedCampaignId)
      void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey, resolvedCampaignId] });
    if (onClose) onClose();
    else navigate('/posts');
  };

  const [markingPublished, setMarkingPublished] = useState(false);
  const [downloading, setDownloading] = useState(false);
  /**
   * Save first, then download.
   *
   * The file is built from Content ROWS, so downloading without saving would hand over a document
   * that quietly disagrees with the editor it was requested from. Saving first is the same rule the
   * design bundle states, applied where the user can actually see the difference.
   */
  const downloadExport = async () => {
    setActionError(null);
    setDownloading(true);
    try {
      const postId = readOnly ? (savedPostId as string) : (await persist()).id;
      await downloadPostExport(postId, exportFormat);
    } catch (err) {
      setActionError(
        err instanceof ApiRequestError ? err.message : 'Could not export this post — please try again.',
      );
    } finally {
      setDownloading(false);
    }
  };

  /** True when this post produces a FILE rather than going to a live destination. */
  const isExportBlog =
    channel === ContentChannel.BLOG && platformLabel === BLOG_EXPORT_DESTINATION;

  const platformFields = () =>
    channel === ContentChannel.SOCIAL
      ? { platform, platformLabel: undefined, exportFormat: undefined }
      : {
          platform: undefined,
          platformLabel: platformLabel || undefined,
          // Only carried for an Export post. Sending it for WordPress would persist a format that
          // nothing reads and that the editor would then show if the destination ever changed.
          exportFormat: isExportBlog ? exportFormat : undefined,
        };

  /**
   * Which blocks a save sends (blocks.ts `persistable`): not an empty photo tile or PDF slot, and on
   * a social post not an empty video slot either. ONE answer for the three places that walk those
   * blocks - the create and both passes of syncContents: were the write pass to count a slot the
   * delete pass skips, it would write to a row that was just deleted.
   */
  const isSaved = (b: EditorBlock) => persistable(b, channel === ContentChannel.SOCIAL);

  /**
   * Give the blocks the ids of the rows a row-by-row save just wrote. `sentKeys` are the keys of
   * the blocks those rows were made from, in the order they were sent - taken where the rows are
   * made, from the form as it was when the save STARTED.
   *
   * Paired by KEY (adoptContentIds), never by position in the form as it is now: a single post is
   * not held still while its save is away. A slot emptied meanwhile ("Remove video") or a picture
   * put in from the Media Library would otherwise move every later id by one, and the next save
   * would delete one piece's row and write its text or file into another's.
   */
  const adoptRowIds = (sentKeys: readonly string[], post: Post) => {
    const rows = post.contents ?? [];
    setBlocks((prev) =>
      adoptContentIds(prev, sentKeys, rows).map((block, i) =>
        // The row's address is the address of the file that was SENT. A slot emptied since keeps
        // its row's id - the next save deletes that row - but is not handed the removed file's
        // address back: the preview would draw it again.
        prev[i].assetId == null && block.assetUrl !== prev[i].assetUrl
          ? { ...block, assetUrl: prev[i].assetUrl }
          : block,
      ),
    );
    setOriginalContentIds([...rows].sort((a, b) => a.order - b.order).map((c) => c.id));
  };

  /**
   * Diff the local blocks against what's persisted and PATCH/POST/DELETE contents. Returns the keys
   * of the blocks it wrote a row for, in the order of those rows (adoptRowIds).
   */
  // `isSaved` above: an empty photo tile, PDF slot or - on a social post - video slot is a
  // dropzone, not content.

  const syncContents = async (postId: string): Promise<string[]> => {
    const keptIds = new Set(
      blocks.filter((b) => b.id && isSaved(b)).map((b) => b.id as string),
    );
    await Promise.all(
      originalContentIds.filter((cid) => !keptIds.has(cid)).map((cid) => api.deleteContent(cid)),
    );
    const rows = blocks.filter(isSaved);
    for (let i = 0; i < rows.length; i += 1) {
      const b = rows[i];
      if (b.id) {
        // Text for every block that saves it (a PDF's title too), and `assetId` verbatim: null must
        // reach the server to CLEAR it, or a removed image comes back on the next load.
        await api.updateContent(b.id, toUpdateContentDto(b, i));
      } else {
        await api.addContent(postId, toCreateContentDto(b, i));
      }
    }
    return rows.map((b) => b.key);
  };

  /** Create-or-update the post (and its campaign, if new) with the current form. */
  /**
   * Create or update the post from the current form state.
   *
   * `allowOverLimit` exists for ONE caller: AI assist needs a saved post id before it can generate,
   * and generating a shorter draft is the natural way OUT of an over-limit post — so that path may
   * create the row. Every user-facing Save refuses, which is what "do not allow them to save"
   * means; relying on the buttons' disabled state alone left three call sites able to bypass it.
   */
  const writePost = async ({ allowOverLimit = false } = {}): Promise<Post> => {
    // The same gates the buttons read (composerGates), asked again here because three call sites do
    // not go through a button. A thread whose shape cannot be stored is refused for every caller.
    const refusal = persistRefusal(gates, allowOverLimit);
    if (refusal) throw new Error(refusal);
    let cid = resolvedCampaignId;
    const makingOneOff = !isEditing && oneOff;
    if (!isEditing && creatingNewCampaign) {
      if (!newCampaignName.trim()) throw new Error('Enter a name for the new campaign.');
      const created = await api.createCampaign({ name: newCampaignName.trim() });
      cid = created.id;
      setCampaignId(created.id);
      setCreatingNewCampaign(false);
      void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
    }
    if (makingOneOff) {
      // No campaign chosen: the server clusters this into the "One-Off Posts" bucket. Only
      // allowed in Per-Post / None modes — mirror the server guard for a clear message.
      if (approvalType === ApprovalType.CAMPAIGN) {
        throw new Error(
          'One-off posts aren’t allowed while approvals are set at the Campaign level — choose or create a campaign.',
        );
      }
      cid = null;
    } else if (!cid) {
      throw new Error('Choose a campaign for this post.');
    }

    const safeTitle = title.trim() || UNTITLED_POST;
    // Bind the chosen connected account so the release-menu publish targets the RIGHT one (a
    // platform can have several). Non-social posts clear it.
    const socialConnectionId =
      channel === ContentChannel.SOCIAL ? (connectionId || null) : null;
    // null = General, an id = that category, undefined = not sent (the server keeps the post's own):
    // only what the field shows is ever saved (categoryForSave).
    const category = categoryForSave(categoryId, categoriesQ.data);
    const postFields = {
      channel,
      title: safeTitle,
      ...platformFields(),
      socialConnectionId,
      ...(category !== undefined ? { categoryId: category } : {}),
    };
    // How the content is written, and which request goes first - decided from what the FORM is now
    // and what is SAVED (contentSavePlan): a post that is not a thread keeps its row-by-row save.
    const how = contentSavePlan({
      isNew: !savedPostId,
      formIsThread: isThreadForm(blocks),
      storedIsThread: storedThread,
    });
    if (savedPostId && how.mode === 'replace') {
      // A thread - or one post saved over a stored thread - is written whole, in ONE request, which
      // either lands or does not. The rows and, beside each, the block it came from are made in one
      // walk BEFORE anything is sent (threadSavePlan), so the ids the server answers with find
      // their blocks by key: a post moved, added or deleted while the request is away adopts
      // nothing that is not its own.
      const plan = threadSavePlan(blocks);
      const writePostRow = async () => {
        const patched = await api.updatePost(savedPostId, postFields);
        // Into the post cache at once, as below: the two requests are not one transaction.
        queryClient.setQueryData(['post', tenantKey, savedPostId], patched);
        return patched;
      };
      const writeContents = async () => {
        const put = await api.replaceContents(savedPostId, { contents: plan.contents });
        queryClient.setQueryData(['post', tenantKey, savedPostId], put);
        setBlocks((prev) => adoptContentIds(prev, plan.keys, put.contents ?? []));
        setOriginalContentIds((put.contents ?? []).map((c) => c.id));
        return put;
      };
      // A thread: the post first, so its platform can take a thread before thread rows arrive. One
      // post over a stored thread: the content first, so the thread rows are gone before the
      // platform or the type changes. The server refuses either the other way round.
      let saved: Post;
      if (how.first === 'post') {
        await writePostRow();
        saved = await writeContents();
      } else {
        await writeContents();
        saved = await writePostRow();
      }
      if (saved.thread !== undefined) setStoredThread(saved.thread != null);
      return saved;
    }
    if (savedPostId) {
      const patched = await api.updatePost(savedPostId, postFields);
      // Push the server's row into the post cache. The save may have changed state the FORM can't
      // predict — a platform change drops `scheduledAt` and stamps `rescheduleRequiredFrom` — and
      // the Schedule tab renders those straight off `existing.data`. Without this the query keeps
      // serving the pre-save row (staleTime 30s), so the composer would still claim the post is
      // scheduled for a slot the server just removed. setQueryData, NOT invalidate: it lands
      // synchronously, and the guarded hydration (same post id) won't re-run and clobber the editor.
      //
      // Seeded from the PATCH's own response BEFORE the content sync, because the two aren't one
      // transaction: if a content call then fails the composer stays open on the error, and it must
      // not keep showing a schedule the server has already deleted. `contents` is momentarily
      // pre-sync, which no render path reads (hydration is id-guarded).
      queryClient.setQueryData(['post', tenantKey, savedPostId], patched);
      const sentKeys = await syncContents(savedPostId);
      const updated = await api.getPost(savedPostId);
      queryClient.setQueryData(['post', tenantKey, savedPostId], updated);
      // Adopt the ids the sync just created, exactly as the create path does. Without this every
      // block added this session still has no `id`, so the NEXT save POSTs it again — a second
      // save (or Save then Publish) duplicated the whole gallery — while originalContentIds stayed
      // stale and drove the delete diff off rows that no longer describe the post.
      adoptRowIds(sentKeys, updated);
      if (updated.thread !== undefined) setStoredThread(updated.thread != null);
      return updated;
    }
    // A new thread is created with the rows a save of it would send, each with its thread post
    // (threadSavePlan); any other new post exactly as before.
    const plan = isThreadForm(blocks) ? threadSavePlan(blocks) : null;
    // The blocks any other new post is created with: its rows, and - by their keys - how the ids
    // of those rows find their blocks afterwards.
    const sentBlocks = blocks.filter(isSaved);
    const created = await api.createPost({
      // Omit campaignId entirely for a one-off (the server assigns the bucket).
      ...(cid ? { campaignId: cid } : {}),
      channel,
      title: safeTitle,
      ...platformFields(),
      socialConnectionId,
      // A new post with no category is on General without being told.
      ...(category ? { categoryId: category } : {}),
      contents: plan
        ? createContentsFromPlan(plan)
        : sentBlocks.map((b, i) => toCreateContentDto(b, i)),
    });
    setSavedPostId(created.id);
    // Created with nothing chosen, the post is on General, and a saved post's field shows what it is
    // saved with. Only while nothing is chosen still: a category picked while the create was away
    // stays, a change to save.
    setCategoryId((chosen) => (chosen === '' ? GENERAL : chosen));
    // Mark this id hydrated so the `existing` query (now enabled on savedPostId) doesn't re-hydrate
    // and clobber the live editor — the form already holds exactly what we just persisted.
    hydratedIdRef.current = created.id;
    if (plan) {
      // By the plan's keys, never by position: see the thread save above.
      setBlocks((prev) => adoptContentIds(prev, plan.keys, created.contents ?? []));
      setOriginalContentIds((created.contents ?? []).map((c) => c.id));
    } else {
      adoptRowIds(sentBlocks.map((b) => b.key), created);
    }
    if (created.thread !== undefined) setStoredThread(created.thread != null);
    return created;
  };

  /**
   * Save the form (writePost), counted while it runs.
   *
   * Every save goes through here - Save, Save and close, the two "save first" prompts, Publish, the
   * export, and the post AI assist and "Add destination" create - so this is the one place that
   * knows a save is in flight. The thread editor holds the SHAPE of a thread still meanwhile (its
   * `busy`): the ids a save comes back with belong to the posts as they were sent.
   */
  const persist = async (options: { allowOverLimit?: boolean } = {}): Promise<Post> => {
    setPersistCount((running) => running + 1);
    try {
      // The scheduling category the post is saved with before this save (null = General).
      const categoryBefore = savedPostId
        ? (queryClient.getQueryData<Post>(['post', tenantKey, savedPostId])?.categoryId ?? null)
        : null;
      const saved = await writePost(options);
      // Saved, an AI conversion in review is over: the thread is an ordinary one now, and the
      // original text and the "Show changes" highlights go with the review (spec section 7). A
      // save that failed has thrown before this, so its review is kept.
      conversion.end();
      // Saved, its words belong to the platform it is saved with: a destination added by hand
      // stops asking for them to be rewritten (copiedText.ts).
      forgetCopiedText(tenantKey, saved.id);
      // Saved into another category: the Suggested Slots on hand (and "no posting times") came
      // from the old category's schedule. They are DROPPED, not just marked out of date: marked,
      // they would still be shown while they are asked for again, and a slot of the old schedule
      // could be picked meanwhile. Asked for again when they are next shown - and only then: each
      // ask is an AI call.
      if (savedPostId && (saved.categoryId ?? null) !== categoryBefore) {
        void queryClient.resetQueries({ queryKey: ['schedule-suggestions', tenantKey, saved.id] });
      }
      return saved;
    } catch (err) {
      // A refused save may have named a scheduling category deleted elsewhere since this tab read
      // the list. Nothing reads the list again on its own (staleTime, no refetch on focus), so the
      // field would go on offering that category and every save would be refused the same way.
      // Read it again: a category that is gone leaves the field, and the next save does not send it
      // (categoryForSave) - the server has already moved the post to General.
      void queryClient.invalidateQueries({ queryKey: ['scheduling-categories', tenantKey] });
      throw err;
    } finally {
      setPersistCount((running) => running - 1);
    }
  };

  /** Ensure the post exists (create if needed), returning its id — for AI generate. */
  const ensurePostId = async (): Promise<string> => {
    if (savedPostId) return savedPostId;
    // AI assist needs an id to generate against. Allowed over the limit on purpose: generating a
    // shorter draft is how a user gets OUT of an over-limit post, so blocking here would leave the
    // only remedy behind the very gate it resolves.
    const created = await persist({ allowOverLimit: true });
    return created.id;
  };

  const applyGenerated = (gen: GeneratedContent) => {
    // A thread whose posts are all live has no text box a draft could go into: nothing changes.
    if (isThreadForm(blocks) && aiTarget == null) return;
    const body = gen.citations.length
      ? `${gen.body}\n\nSources:\n${gen.citations.map((c) => `- ${c.text} — ${c.url}`).join('\n')}`
      : gen.body;
    // Into the text box the AI assist targets, as the form is when the draft arrives; a thread is
    // never given a block here (applyGeneratedText). The spare box - for a post left with no text
    // box at all - is made here, not in the updater, which runs twice under StrictMode.
    const spare = newTextBlock();
    setBlocks((prev) => applyGeneratedText(prev, body, focusedKey, focusedTextKey, spare));
    if (gen.title) setTitle(gen.title);
  };

  /**
   * Save from the Content tab, then move to Schedule.
   *
   * Saving used to CLOSE the composer, which made writing a post and scheduling it two separate
   * trips through the same modal — and left a saved-but-unscheduled post behind every time someone
   * only wanted to write. Advancing keeps the natural order (write, then place it, then send it for
   * approval) in one pass; "Save and close" is always available for anyone who is done.
   */
  const save = useMutation({
    mutationFn: () => persist(),
    onSuccess: () => {
      // The saved text now belongs to the saved platform — re-baseline, or the rewrite notice
      // stays up for a change that has already been persisted (this save keeps the modal open).
      setTextPlatform(channel === ContentChannel.SOCIAL ? platform : null);
      setActiveTab('schedule');
    },
    onError: (err) =>
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Failed to save'),
  });
  /** Save and leave — the way out, on every tab. */
  const saveAndClose = useMutation({
    mutationFn: () => persist(),
    onSuccess: closeOrNavigate,
    onError: (err) =>
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Failed to save'),
  });
  // Save WITHOUT closing — used by the Schedule/Approval "save first" prompts on a new post, so the
  // post gets created (savedPostId set) and those tabs go live in the same session.
  const saveInPlace = useMutation({
    mutationFn: () => persist(),
    // The saved text now belongs to the saved platform — re-baseline, or the rewrite notice
    // would stay up for a change that has already been persisted (this save keeps the modal open).
    onSuccess: () => setTextPlatform(channel === ContentChannel.SOCIAL ? platform : null),
    onError: (err) =>
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Failed to save'),
  });

  const watchLostPublish = useLostPublishWatch();
  const publish = useMutation({
    mutationFn: async () => {
      if (gates.nothingToSend) {
        // Every post of the thread is already live, so nothing is sent and no account is needed:
        // the release endpoint records the thread as published. This is also the way to finish a
        // thread whose account is gone - delete the posts that were not sent, save, publish.
        const finishedId = readOnly ? savedPostId : (await persist()).id;
        if (!finishedId) throw new Error('Save the post before publishing.');
        return sendPublish(finishedId, () => api.publishPost(finishedId));
      }
      if (!connectionId) throw new Error('Choose a connected account to publish to.');
      // A releasable post (APPROVED/SCHEDULED) is read-only, so there are no edits to
      // persist and re-saving it would 400 on the server's edit gate (assertEditable).
      // Publish the already-saved post directly; only persist when the form is editable
      // (unsaved edits, or a brand-new NONE-mode post that must be created first).
      const postId = readOnly ? savedPostId : (await persist()).id;
      if (!postId) throw new Error('Save the post before publishing.');
      // Only the publish itself is watched for a lost answer (sendPublish): the save before it is
      // not a publish, and what it refuses with is said as it is.
      return sendPublish(postId, () => api.publishPostNow(postId, connectionId));
    },
    onMutate: () => {
      sendingRef.current = true;
      // The failure the post already shows, if any: what a lost publish is later compared against.
      return { errorBefore: existing.data?.publishError ?? null };
    },
    onSuccess: closeOrNavigate,
    onError: (err, _nothing, sent) => {
      // A lost publish says so in its own words (LostPublishError's message).
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Publish failed');
      if (err instanceof LostPublishError) {
        // No answer came back, so the post may still be going out (lostRequest.ts). Read it again
        // until it shows how the publish ended, then show that: the post as published, or its own
        // failure reason in place of the line above. It is only ever read - never sent again.
        watchLostPublish({
          read: () => api.getPost(err.postId),
          errorBefore: sent?.errorBefore ?? null,
          onResult: (post) => {
            // setQueryData, as after scheduling: the form is not filled again from it.
            queryClient.setQueryData(['post', tenantKey, post.id], post);
            setActionError(post.publishedAt ? null : post.publishError);
            void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
            void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
            void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey] });
            void queryClient.invalidateQueries({ queryKey: ['calendar', tenantKey] });
            void queryClient.invalidateQueries({ queryKey: ['post-group', tenantKey] });
          },
        });
      }
      // A thread can stop part-way, with its first posts live. Read the post again so that they
      // are locked in the form (and a post nobody could confirm is asked about); the form itself
      // is kept - filling it is guarded by the post's id. Returned, so that the publish - and the
      // thread's text and media held still for it - is not over until those locks are here.
      if (gates.thread) return queryClient.invalidateQueries({ queryKey: ['post', tenantKey] });
    },
    onSettled: () => {
      sendingRef.current = false;
    },
  });

  // A scheduling action (manual date, queue placement, or applying a suggested slot) can happen
  // while the post is still a draft, so DON'T close — push the updated post into the cache
  /**
   * The scheduling decision just applied to THIS destination, kept so it can be offered to the rest
   * of the set.
   *
   * Cleared as soon as it is offered and taken, and replaced whenever a new decision is made — the
   * button asks about the choice you just made, so a stale one would apply something you have since
   * changed your mind about.
   */
  const rememberDecision = (decision: ApplyScheduleToSetDto) => {
    setSetDecision(decision);
    setSetResult(null);
  };

  // (setQueryData, not invalidate, so the guarded hydration doesn't re-run and clobber unsaved
  // edits) and refresh the list/calendar/suggestions surfaces.
  const applyScheduleResult = (updated: Post) => {
    setActionError(null);
    // The post now has a time (or a queue placement, or "as soon as approved"), so approval is the
    // next thing to do with it. Revealed rather than always shown: before there is a schedule the
    // button would jump to a tab with nothing to act on.
    setScheduled(true);
    queryClient.setQueryData(['post', tenantKey, updated.id], updated);
    void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['calendar', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['schedule-suggestions', tenantKey, updated.id] });
    if (resolvedCampaignId)
      void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey, resolvedCampaignId] });
  };

  // Set a specific date/time OR apply an AI-suggested slot (both are exact times → pinned).
  const schedule = useMutation({
    mutationFn: (iso: string) => api.schedulePost(savedPostId as string, iso),
    onSuccess: (post, iso) => {
      applyScheduleResult(post);
      // An exact time is the one decision that copies VERBATIM to the rest of the set.
      rememberDecision({ strategy: 'exact', at: iso });
      // A suggested slot is an exact time too, but it is ITS OWN row — the highlight belongs on the
      // slot the user clicked, not on the date picker they never opened.
      setChosenWhen(whenMode === 'date' ? 'date' : null);
    },
    onError: (err) =>
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Could not schedule'),
  });
  // "Post as soon as approved" (POST mode): no fixed time — publishes on final approval.
  const setPublishWhenApproved = useMutation({
    mutationFn: (value: boolean) => api.setPostPublishWhenApproved(savedPostId as string, value),
    onSuccess: applyScheduleResult,
    onError: (err) =>
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Could not update'),
  });
  // Queue placement: Next Available / Prioritize / Pick a Month (queued=true, movable).
  const enqueue = useMutation({
    mutationFn: (dto: EnqueuePostDto) => api.enqueuePost(savedPostId as string, dto),
    onSuccess: (post, dto) => {
      applyScheduleResult(post);
      // The PLACEMENT is remembered, not where it landed — each sibling resolves its own.
      rememberDecision({ strategy: dto.strategy, ...(dto.month ? { month: dto.month } : {}) });
      setChosenWhen(dto.strategy === 'month' ? 'month' : dto.strategy);
    },
    onError: (err) =>
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Could not place in queue'),
  });
  /**
   * The set this post belongs to.
   *
   * Same query key as the destination strip above, so this costs no extra request — it is the strip
   * that already knows how many destinations there are.
   */
  const groupQuery = useQuery({
    queryKey: ['post-group', tenantKey, existing.data?.postGroupId],
    queryFn: ({ signal }) => api.getPostGroup(existing.data?.postGroupId as string, signal),
    enabled: Boolean(existing.data?.postGroupId) && tenantKey !== '',
  });
  /** Other live destinations — the posts an "apply to the set" would actually move. */
  const siblingCount = Math.max(0, (groupQuery.data?.destinations.length ?? 0) - 1);

  const applyToSet = useMutation({
    mutationFn: (dto: ApplyScheduleToSetDto) => api.applyScheduleToSet(savedPostId as string, dto),
    onSuccess: (result) => {
      setSetResult(result);
      // Sibling times just moved: the strip shows them, and so do the list and calendar.
      void queryClient.invalidateQueries({ queryKey: ['post-group', tenantKey, existing.data?.postGroupId] });
      void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', tenantKey] });
    },
    onError: (err) =>
      setActionError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : 'Could not apply those settings to the set',
      ),
  });

  /**
   * "Apply Same Media to Set?" — the media panel's media onto every other destination, trimmed to
   * what each platform accepts. The outcome is shown above the panel it was applied from, so it is
   * keyed by that panel.
   *
   * It saves this post first, exactly as "Save changes" does - the same persist(), held by the same
   * gates, staying on this tab - and applies only once that save is in. The media was just arranged
   * here: applied to every sibling while this post itself stayed unsaved, closing the modal would
   * lose it from the one post it came from. While the gates hold Save, the button is held too, with
   * the reason under it (MediaToSetControls.heldReason); a save that fails all the same throws, so
   * nothing is applied, and why is said on the composer's error line, where a failed Save says it.
   */
  const [mediaSetNotice, setMediaSetNotice] = useState<{
    assetIds: string[];
    result: ApplyMediaToSetResult;
  } | null>(null);
  const applyMediaToSet = useMutation({
    mutationFn: async (assetIds: string[]) => {
      const saved = await persist();
      // As after "Save changes", which also keeps the modal open: the saved text now belongs to the
      // saved platform, so the rewrite notice is re-baselined.
      setTextPlatform(channel === ContentChannel.SOCIAL ? platform : null);
      return api.applyMediaToSet(saved.id, assetIds);
    },
    onMutate: () => {
      setMediaSetNotice(null);
      setActionError(null);
    },
    onSuccess: (result, assetIds) => {
      setMediaSetNotice({ assetIds, result });
      // REMOVE, not invalidate, every sibling that changed. Their content rows were replaced, so a
      // cached copy is now wrong in a way that bites: opening that tab would hydrate the editor from
      // it once (hydration is guarded per post id) and a save would then address content ids that no
      // longer exist. With no cached copy the tab waits for the real one. This post is left alone,
      // so nothing here can touch what the author is still editing.
      const changed = new Set([
        ...result.outcomes.filter((o) => o.applied).map((o) => o.postId),
        ...result.reopened,
      ]);
      for (const postId of changed) {
        queryClient.removeQueries({ queryKey: ['post', tenantKey, postId], exact: true });
      }
      // Thumbnails and states show on the strip, the lists, the calendar and the campaign page.
      void queryClient.invalidateQueries({ queryKey: ['post-group', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey] });
      // Reopening siblings recomputes their campaign's state, which the campaigns list shows.
      void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
    },
    onError: (err) =>
      setActionError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : 'Could not apply that media to the set',
      ),
  });

  const publishNonSocial = useMutation({
    // Persist first when editable (mirrors the social `publish` mutation) so an unsaved channel
    // flip / content edit is saved BEFORE publishing — otherwise the server would publish the
    // stale saved post (e.g. still routed as SOCIAL) and drop the edits.
    mutationFn: async () => {
      const postId = readOnly ? (savedPostId as string) : (await persist()).id;
      return api.publishPost(postId);
    },
    onSuccess: closeOrNavigate,
    onError: (err) =>
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Publish failed'),
  });

  const generate = useMutation({
    mutationFn: async (mode: GenerateContentDto['mode']) => {
      const postId = await ensurePostId();
      const dto: GenerateContentDto = {
        channel,
        platform: channel === ContentChannel.SOCIAL ? platform : undefined,
        mode,
        prompt: title.trim() || undefined,
        outline: focusedTextBlock?.text.trim() || undefined,
      };
      return api.generatePost(postId, dto);
    },
    onMutate: () => setNeverSayTerms([]),
    onSuccess: (gen) => {
      applyGenerated(gen);
      setNeverSayTerms(gen.reviewTerms ?? []);
      void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
    },
    onError: (err) =>
      setActionError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Generation failed'),
  });

  const selectedAccount = accounts.find((a) => a.id === connectionId) ?? null;
  // The account chosen under Platform cannot publish until it is reconnected (the Connections
  // page's own "Reconnect needed"): the note under the picker says how.
  const reconnectAccount =
    selectedAccount && reconnectNeeded(selectedAccount) ? selectedAccount : null;

  // The destination's hard length cap — social platforms only; Blog/Video/Podcast aren't capped.
  //
  // Keyed on the DESTINATION the user can actually see, not the `platform` state: that state starts
  // on a placeholder (LINKEDIN) which survives whenever no connected account replaces it, so a
  // workspace with no accounts would be measured — and blocked — against a 3,000-char LinkedIn cap
  // for a destination it never chose and cannot change. No visible destination → no cap, no gate.
  const destinationPlatform =
    channel === ContentChannel.SOCIAL
      ? (selectedAccount?.platform ?? existing.data?.platform ?? null)
      : null;
  const charLimit = socialCharLimit(destinationPlatform);
  // How many images this post may carry (X 4, everywhere else 20). Enforced at compose time so a
  // gallery the destination would refuse can't be built; the publisher caps again as a backstop.
  const imageLimit = socialImageLimit(destinationPlatform);

  /**
   * "Convert to thread with AI": the card under the text of a single X or Threads post that is over
   * its limit, the request, and the review of the thread it brings (useThreadConversion). Judged by
   * the destination the counters judge by - never the placeholder `platform`. Kept here, not in the
   * editor: the editor is drawn on the Content tab only, and a conversion on its way, or in review,
   * outlives a tab switch. Nothing is created or saved by it; the modal closing ends it.
   */
  const conversion = useThreadConversion({
    blocks,
    setBlocks: setEditorBlocks,
    platform: destinationPlatform,
    channel,
    readOnly,
  });
  /**
   * The "Content" label of a social post: where focus goes when the AI banner above it is
   * dismissed, rather than to the page body with the Dismiss that had it.
   */
  const contentLabelRef = useRef<HTMLParagraphElement>(null);

  const busy =
    // A file still on its way into the post: its slot is empty until it lands, so a Save or Publish
    // now would go out without it.
    uploading ||
    save.isPending ||
    saveInPlace.isPending ||
    // On every tab: nothing on the Schedule tab starts while the post is saved and the composer closes.
    saveAndClose.isPending ||
    // It saves the post first (see applyMediaToSet), so it holds what a save holds.
    applyMediaToSet.isPending ||
    publish.isPending ||
    generate.isPending ||
    // The same kind of AI call as the draft above: what it brings replaces the post's text.
    conversion.converting ||
    schedule.isPending ||
    enqueue.isPending ||
    setPublishWhenApproved.isPending ||
    publishNonSocial.isPending;
  // What the thread editor is told is narrower, and is NOT `busy` above (which also waits for an
  // upload and for scheduling): a save, a publish or an AI call is in flight, so the shape of a
  // thread - adding, moving and deleting its posts - holds still until it is back. The AI card's
  // Convert and Retry, and the AI banner's Undo and Regenerate, wait on it too.
  const editorBusy =
    persistCount > 0 || publish.isPending || generate.isPending || conversion.converting;

  // The field shows a scheduling category the post is not saved with. The queue and the Suggested
  // Slots work from the category the post is SAVED with (the server reads it off the post), so they
  // would place the post on the old category's times: the Schedule tab holds them and asks for a
  // save first. Judged only once the form was filled from an opened post - until then the field
  // shows its starting value, which is no change - and only for a category the field can show,
  // which is all a save would send (categoryForSave).
  const categoryShown = categoryForSave(categoryId, categoriesQ.data);
  const categoryUnsaved =
    !readOnly &&
    existing.data != null &&
    (!id || hydratedId === id) &&
    categoryShown !== undefined &&
    categoryShown !== (existing.data.categoryId ?? null);
  /**
   * The save that hold asked for is on its way - the strip's Save, or "Save and close" while the
   * category is unsaved. The hold lasts until it is in: the post row is written before the content,
   * and the queue and Suggested Slots released part-way would ask for the suggestions there and
   * then, and once more when the save drops them (persist) - two AI calls - showing the old
   * category's slots meanwhile.
   */
  const [categorySaving, setCategorySaving] = useState(false);
  const queueHeld = categoryUnsaved || categorySaving;
  /**
   * Keyboard focus when the strip is replaced. Its Save is what usually had focus, and it goes with
   * the strip, which leaves focus on the page body, outside the composer. The first queue action
   * takes it when it can be used then (Next Available), else the Schedule tab itself. Only focus
   * that was lost moves: someone who has gone on to something else meanwhile keeps their place.
   */
  const nextAvailableRef = useRef<HTMLButtonElement>(null);
  const schedulePanelRef = useRef<HTMLDivElement>(null);
  const queueWasHeld = useRef(queueHeld);
  useEffect(() => {
    const replaced = queueWasHeld.current && !queueHeld;
    queueWasHeld.current = queueHeld;
    // Off the Schedule tab neither is drawn, so nothing moves.
    if (!replaced) return;
    const focused = document.activeElement;
    if (focused && focused !== document.body) return;
    const next = nextAvailableRef.current;
    (next && !next.disabled ? next : schedulePanelRef.current)?.focus();
  }, [queueHeld]);
  // AI "Suggested Slots" — fetched lazily when the Schedule tab is open on a schedulable post, and
  // not while they would come from a category the post is leaving (queueHeld).
  const suggestionsQ = useQuery({
    queryKey: ['schedule-suggestions', tenantKey, savedPostId],
    queryFn: ({ signal }) => api.getScheduleSuggestions(savedPostId as string, signal),
    enabled: activeTab === 'schedule' && canSchedule && Boolean(savedPostId) && !queueHeld,
  });
  // The queue (Next Available / Prioritize / Pick a Month) needs an enabled posting schedule; when
  // there is none the API 400s, so disable those rows until it loads and confirms one exists.
  const scheduleEmpty = suggestionsQ.data?.scheduleEmpty ?? true;
  /**
   * The category whose times the post's queue and Suggested Slots use, as "no posting times" names
   * it: the one the post is saved with, unless the loaded list no longer has it - the server counts
   * a category it does not have as none - and null for General.
   */
  const savedCategoryId = existing.data?.categoryId ?? null;
  const savedCategoryName =
    savedCategoryId === null
      ? null
      : categoriesQ.data
        ? (categoriesQ.data.find((c) => c.id === savedCategoryId)?.name ?? null)
        : (existing.data?.category?.name ?? null);
  // Constrain the "Pick a Month" picker to the supported window (this month … ~13 months out) so it
  // mirrors the server's month horizon and never posts a month the API can't reach.
  const monthBounds = useMemo(() => {
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const max = new Date();
    max.setMonth(max.getMonth() + 13);
    return { min: fmt(new Date()), max: fmt(max) };
  }, []);

  /**
   * The earliest time the picker will accept, and whether what is in it has already gone by.
   *
   * Recomputed each render, so a modal left open past its own floor tightens rather than going
   * stale. Both are cheap; neither needs memoising.
   */
  const scheduleFloor = nextQuarterHour(new Date());
  const pickedTimeIsPast = isInPast(scheduleAt);

  /**
   * Whether this post's own scheduled time came and went while it waited.
   *
   * Read off the LIVE post rather than the form: the banner is about what is saved, and the form
   * may hold an edit that has not been committed.
   */
  const scheduleHasPassed = existing.data ? isPostOverdue(existing.data) : false;

  // ── Preview tab ─────────────────────────────────────────────────────
  // Follows the destination the user can see (the destination tab, or the account picker on a
  // single post). While the post is editable it shows the form as it stands, debounced so it keeps
  // up without redrawing on every keystroke; once the post is locked it shows the post as saved.
  // The account is the one the picker holds - on an approved post that is still locked, the one
  // "Publish now" will send it as - falling back to the saved one until the picker has it.
  // The title is no part of what is drawn: it is internal, and is never published.
  // Filling the form from the saved post replaces it outright, so that takes effect at once.
  const liveForm = useMemo(() => ({ blocks }), [blocks]);
  const settledForm = useDebounced(liveForm, 200, hydratedId);
  // The post as saved: a thread as its posts, under keys that come from its rows, so they are the
  // same each time this is worked out again (savedPostBlocks).
  const savedBlocks = useMemo(
    () => (existing.data ? savedPostBlocks(existing.data) : null),
    [existing.data],
  );
  // Which post is drawn (choosePreview's rule) and as what: one card, or a thread post by post.
  const model = previewModel({
    locked: readOnly,
    openedId: id,
    hydratedId,
    saved: savedBlocks ? { blocks: savedBlocks } : null,
    form: settledForm,
  });
  // The leading picture, for a post that shows ONE. With two or more the card draws a grid whose
  // cells each load their own small copy, so the original is not fetched for nothing.
  const previewImage = useThumbnailSrc(
    model.kind === 'single' && model.source.images.length < 2 ? model.source.mediaUrl : null,
  );
  // `model` is a new object on every render, so it cannot be the dependency; the chosen blocks array is.
  const threadBlocks = model.kind === 'thread' ? model.blocks : null;
  const previewPosts = useMemo(
    () => (threadBlocks ? threadPreviewPosts(threadBlocks) : []),
    [threadBlocks],
  );
  const previewConnection =
    selectedAccount ?? accounts.find((a) => a.id === existing.data?.socialConnectionId) ?? null;
  const previewAccount = {
    // The post's account was disconnected since: its name went with it.
    name: previewConnection?.accountName ?? 'Disconnected account',
    handle: previewConnection?.accountHandle ?? null,
  };
  /**
   * The post as the editor holds it NOW, for the top of Generate media and Generate design: what
   * someone has just typed is what they expect a prompt drafted from, saved or not.
   */
  const accountLabel = previewConnection?.accountName ?? null;
  const postContext = useMemo<PostContext>(
    () => ({
      text: blocks
        .filter((b) => b.kind === ContentKind.TEXT && b.text.trim())
        .map((b) => b.text.trim())
        .join('\n\n'),
      platform: destinationPlatform,
      accountLabel,
      media: blocks
        .filter((b) => b.assetId && (b.kind === ContentKind.IMAGE || b.kind === ContentKind.VIDEO))
        .map((b) => ({
          id: b.key,
          url: b.assetThumbUrl ?? b.assetPosterUrl ?? b.assetUrl ?? `/api/assets/${b.assetId}/image`,
          mimeType: b.assetMimeType,
        })),
    }),
    [blocks, destinationPlatform, accountLabel],
  );
  /** "Save and return to post": the finished image goes into the gallery it was asked for from. */
  const saveToPost: SaveToPost = (asset, note) => {
    const anchorKey = generateFor?.anchorKey ?? null;
    const tile = newImageBlock();
    setEditorBlocks((prev) =>
      attachToPost(prev, anchorKey, asset, imageLimit, tile, {
        social: channel === ContentChannel.SOCIAL,
      }),
    );
    setMediaNotice(note ?? null);
  };
  // The workspace's own logo in the preview's avatar circle - its icon, else its main logo - loaded
  // as the workspace switcher loads it (behind sign-in, as an object URL). With no logo, or until it
  // has loaded, the preview keeps its initials.
  const workspaceLogos = activeTenant?.brandAssets?.logos;
  const previewLogo = useProxyImage(workspaceLogos?.icon ?? workspaceLogos?.primary ?? null);
  const previewPanel =
    channel !== ContentChannel.SOCIAL ? (
      <p className="text-sm text-ink-muted">Previews are for social posts.</p>
    ) : !isPreviewPlatform(destinationPlatform) ? (
      <p className="text-sm text-ink-muted">
        {destinationPlatform
          ? 'A preview is not available for this destination yet.'
          : 'Choose an account to see how this post will look.'}
      </p>
    ) : connectionsQ.isLoading ? (
      <p className="text-sm text-ink-muted">Loading preview…</p>
    ) : model.kind === 'thread' ? (
      destinationPlatform === SocialPlatform.X || destinationPlatform === SocialPlatform.THREADS ? (
        <ThreadPreview
          platform={destinationPlatform}
          account={previewAccount}
          avatarUrl={previewLogo}
          posts={previewPosts}
        />
      ) : (
        <p className="text-sm text-ink-muted">{THREAD_PLATFORM_MESSAGE}</p>
      )
    ) : model.source.empty ? (
      // Nothing to draw yet - and never the post's title in its place: that is not what goes out.
      <p className="text-sm text-ink-muted">{EMPTY_PREVIEW_MESSAGE}</p>
    ) : (
      <PlatformPreview
        // A new destination starts folded, the way its own feed would first show the post.
        key={destinationPlatform}
        platform={destinationPlatform}
        account={previewAccount}
        avatarUrl={previewLogo}
        text={model.source.text}
        hashtags={model.source.hashtags}
        imageUrl={previewImage}
        pdf={model.source.document}
        images={model.source.images}
      />
    );
  /**
   * Every gate on Save, Schedule, the queue and Publish, from ONE function (composerGates) - so the
   * buttons, the lines that say why they are held and persist() cannot disagree about the same form.
   *
   * A post that is not a thread is held exactly as it was before threads:
   *  - `saveError` (red, BLOCKS SAVE): ANY over-length text piece - deliberately every piece, not
   *    just the one that publishes, because the counter is shown per piece and a red count that
   *    still saved would be worse - or more images than the destination takes. `imageLimit` follows
   *    the destination, so a gallery built for LinkedIn (20) becomes over-cap the moment the post
   *    is re-pointed at X (4); disabling the ADD controls catches nothing of that.
   *  - `publishWarning` (amber, BLOCKS PUBLISH, NOT SAVE): a social post with no text - it is never
   *    sent, and its title never stands in for the text it does not have - then the destination's
   *    composition rules, asked of the same function the Add buttons ask. A post that already
   *    breaks one - written before the rule existed, or re-pointed at a stricter destination - must
   *    stay saveable, or fixing it would be impossible.
   *
   * A thread is judged post by post instead, by the rules the server applies to the same rows, and
   * can also be held because a post is still empty (`saveHint`), because a post's outcome is not
   * known (`unconfirmedHold`) or because the account cannot publish a thread (`permissionHold`).
   *
   * Either is held, Save included, while a file is still on its way into it (`uploadHold`).
   */
  const gates = composerGates({
    blocks,
    platform: destinationPlatform,
    channel,
    charLimit,
    imageLimit,
    account: selectedAccount,
    stored: existing.data?.thread,
    uploading,
  });
  const saveBlocked = isSaveBlocked(gates);
  const publishBlocked = isPublishBlocked(gates);
  const publishHold = publishHoldReason(gates);
  const { nothingToSend } = gates;
  // While the form is a thread: why Type and some accounts cannot be chosen, whether part of it can
  // no longer change, and the line beside "Content".
  const threadSwitch = threadSwitchBlock(blocks);
  const hasLockedPost = anyPostLocked(blocks);
  const threadSummary = threadSummaryLine(blocks);

  /**
   * Take an approved or scheduled post back to draft so it can be edited, clearing its schedule.
   * POST mode only — under CAMPAIGN approval the campaign is what was signed off, and reopening one
   * post would mean unscheduling every sibling in it.
   */
  const reopen = useMutation({
    mutationFn: () => api.reopenPost(savedPostId as string),
    onSuccess: (post) => {
      queryClient.setQueryData(['post', tenantKey, post.id], post);
      void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
      // The campaign page's lane and the set's tabs show this post's state too.
      void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['post-group', tenantKey] });
    },
    // Reported through the composer's own error line, which every other action here uses.
    onError: (err) =>
      setActionError(
        err instanceof ApiRequestError ? err.message : 'Could not reopen this post — please try again.',
      ),
  });

  /**
   * Save the account of a post that has no Save button - approved, scheduled or failed. The server
   * allows it without touching approval (owner's ruling: same platform), so a post whose account had
   * to be reconnected or was removed can be pointed at it again and retried, without reopening.
   */
  const setAccount = useMutation({
    mutationFn: (socialConnectionId: string) =>
      api.setPostAccount(savedPostId as string, socialConnectionId),
    onSuccess: (post) => {
      setActionError(null);
      queryClient.setQueryData(['post', tenantKey, post.id], post);
      void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['post-group', tenantKey] });
    },
    onError: (err) => {
      // Back to what is saved: the picker must never show an account the post doesn't have. The
      // platform too - choosing the account already moved it, and every save sends it
      // (platformFields), so one left behind would have each later save refused as a change of
      // platform.
      setConnectionId(existing.data?.socialConnectionId ?? null);
      if (existing.data?.platform) setPlatform(existing.data.platform);
      setActionError(
        err instanceof ApiRequestError ? err.message : 'Could not change the account — please try again.',
      );
    },
  });

  const askReopen = async () => {
    const ok = await confirm({
      title: 'Reopen this post for editing?',
      message:
        'It goes back to draft and its scheduled time is cleared, so it will not publish until you schedule it again. Its approval has to be requested afresh.',
      confirmLabel: 'Reopen',
    });
    if (ok) reopen.mutate();
  };

  // The panel targets ONE gallery, identified by its anchor block. Close it when that exact
  // gallery is gone (not merely when SOME gallery remains) or the post became read-only —
  // otherwise Insert would be a button that silently does nothing.
  const anchorAlive =
    mediaPicker != null && blocks.some((b) => b.key === mediaPicker.anchorKey);
  useEffect(() => {
    if (mediaPicker && (!anchorAlive || readOnly)) setMediaPicker(null);
  }, [mediaPicker, anchorAlive, readOnly]);
  // How many more images that gallery can take, and what its post already holds - counted over
  // the gallery's own post in a thread, where the cap and the rule against photos beside video are
  // per post, else over the whole post as before (mediaSlotFor). With no library open there is no
  // gallery to name, and the answer is not read.
  const mediaSlot = mediaSlotFor(blocks, mediaPicker?.anchorKey ?? '', imageLimit);

  // publishBlocked included: scheduling a post that cannot go out only DEFERS the rejection — the
  // auto-publish worker would deliver it and the platform would refuse, landing the post in
  // publishError. Refusing here is the same promise the disabled Publish button makes.
  const queueDisabled = busy || suggestionsQ.isLoading || scheduleEmpty || publishBlocked;
  const hasImage = blocks.some(
    (b) => b.kind === ContentKind.IMAGE && (Boolean(b.assetId) || Boolean(b.assetUrl)),
  );
  const canPublish =
    selectedAccount != null &&
    (PUBLISHABLE_PLATFORMS.has(selectedAccount.platform) ||
      (IMAGE_PUBLISH_PLATFORMS.has(selectedAccount.platform) && hasImage));
  // Both "Publish now" controls are held by this one answer (isPublishNowHeld). A thread with
  // nothing left to send needs no account. A post with no text is held by the gates themselves -
  // every social post needs text, on every platform - so nothing here asks about a text box.
  const publishNowHeld = isPublishNowHeld(gates, { canPublish, busy });

  /**
   * Opted in, but the worker still skips this workspace. It treats "approvals off" as releasing a
   * post ONLY for a genuine NONE workspace — if approvals are off merely by the install-wide
   * override while the workspace itself is still configured for approvals, those posts were never
   * released through any approval flow, so it deliberately leaves them alone. Mirrors the
   * `else { continue }` branch in post-release.service, and is otherwise invisible.
   */
  const autoPublishOverrideGap =
    activeTenant != null &&
    activeTenant.autoPublishEnabled &&
    approvalType === ApprovalType.NONE &&
    activeTenant.approvalType !== ApprovalType.NONE;

  /**
   * Why "Publish now" is unavailable — the ACTUAL blocker, not a catch-all. The previous copy
   * always tacked on "(image platforms need an image piece)", which reads as "LinkedIn needs an
   * image" when the real problem is simply that no account is chosen. Only Instagram requires an
   * image; LinkedIn/Facebook/X/Threads publish text-only.
   */
  const publishBlockedReason = useMemo(() => {
    if (channel !== ContentChannel.SOCIAL) return null;
    // Checked BEFORE canPublish: an over-limit post can be otherwise publishable, and the
    // Content-tab message lives in the editable branch — so on a read-only post this is the only
    // thing that explains why Publish Now went dead. The first of what holds a save, then the
    // destination's own rules (a PDF anywhere but LinkedIn, photos beside a video...) - the same
    // ones that hold Schedule, so Publish now can't confirm a post the server will then refuse -
    // then, for a thread, a post whose outcome is not known and an account that cannot publish one.
    // Said for a post that is not releasable yet as well: the same hold greys every way to set a
    // time on a saved draft that still waits for approval, and nothing else on this tab says why.
    if (publishHold) return publishHold;
    // What follows is about "Publish now", which only a releasable post has.
    if (!releasable) return null;
    // Every post of the thread is already live: nothing will be sent, so no account is needed and
    // the nudges below do not apply.
    if (nothingToSend) return null;
    if (canPublish) return null;
    // Don't flash a "choose an account" nudge while the connections query is still in flight.
    if (connectionsQ.isLoading) return null;
    if (accounts.length === 0) return 'Connect a social account to publish now.';
    if (!selectedAccount) return 'Choose a connected account on the Content tab to publish now.';
    if (IMAGE_PUBLISH_PLATFORMS.has(selectedAccount.platform)) {
      return `${platformName(selectedAccount.platform)} needs an image piece to publish — add one on the Content tab.`;
    }
    return `Publishing to ${platformName(selectedAccount.platform)} isn’t supported yet.`;
  }, [releasable, channel, canPublish, publishHold, nothingToSend, connectionsQ.isLoading, accounts.length, selectedAccount]);

  if (id && existing.isLoading) {
    return (
      <>
        <TestModeBanner />
        <LoadingState label="Loading post…" />
      </>
    );
  }
  if (id && existing.isError) {
    return (
      <>
        <TestModeBanner />
        <ErrorState
          message={existing.error instanceof ApiRequestError ? existing.error.message : 'Could not load post.'}
          onRetry={() => void existing.refetch()}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* Above the destination strip: this decides whether anything scheduled here will actually go
          out, so it is read before the post is written, not after. INSIDE this root, not above it in
          ComposerModal: the bar at the bottom of this root is `sticky`, and a sticky box cannot rise
          above its containing block. With the banner outside, a small phone opened the composer with
          the bar - Cancel, Save and Save and close - below the fold until someone scrolled. */}
      <TestModeBanner />
      {/*
        Destinations, for SOCIAL posts only.

        Hidden on Blog/Video/Podcast because a destination set is a set of social ACCOUNTS: those
        channels publish to a labelled destination or to a file, so a picker of connected accounts
        has nothing to offer them.

        Shown on a NEW post too, not just an edit. Adding one saves the post first (ensurePostId) —
        a destination is seeded by copying an existing post, so there has to be one — after which
        the real tabs take over.
      */}
      {/*
        Its time came and went while it waited — almost always for an approval that arrived too
        late. Nothing will publish it now, and nothing else on the Schedule tab says so as plainly
        as this: the status line reads "Queued for" a date in the past, which looks like a plan
        rather than a problem.
      */}
      {scheduleHasPassed && (
        <AlertBanner tone="warning">
          This post is scheduled for a date and time that has already passed. Please reschedule it
          for a future date and time.
        </AlertBanner>
      )}

      {channel === ContentChannel.SOCIAL && (
        <DestinationTabs
          part="tabs"
          postId={effectiveId ?? null}
          groupId={existing.data?.postGroupId ?? null}
          currentConnectionId={connectionId}
          currentPlatform={platform}
          // Outside the modal there is no `?compose=` to re-point, so navigate to the sibling
          // instead of leaving a tab that looks clickable and does nothing.
          onSelect={(siblingId) =>
            onSelectSibling ? onSelectSibling(siblingId) : navigate(`/posts?compose=${siblingId}`)
          }
          ensurePostId={ensurePostId}
        />
      )}
      {/* Tabs — shown for both new and existing posts. On a brand-new post the Schedule/Approval
          tabs prompt to save first, then go live the moment the post is saved. */}
      {/* flex-wrap: the three tabs are wider than the narrowest phones; the last one drops to a
          second line there rather than pushing the composer sideways. */}
      <div className="flex flex-wrap gap-1 border-b border-line">
        {(['content', 'schedule', 'approval'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActiveTab(t)}
            className={cn(
              'focus-ring -mb-px border-b-2 px-4 py-2 text-sm font-medium',
              // Forced colours (Windows High Contrast) paint every underline alike: the current
              // tab's takes a system colour, and the others' are painted out.
              activeTab === t
                ? cn('border-action text-ink', hcUnderlineSelected)
                : cn('border-transparent text-ink-muted hover:text-ink', hcUnderlineIdle),
            )}
          >
            {t === 'content' ? 'Content' : t === 'schedule' ? 'Schedule' : 'Approval'}
          </button>
        ))}
      </div>

      {activeTab === 'content' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/*
              NOT a <label> wrapper. The designed Select renders a hidden, aria-hidden native
              <select> BEFORE its trigger button, so a label with no htmlFor binds to that one —
              clicking the word would focus a control nobody can see instead of opening the listbox.
              The aria-label on the Select carries the accessible name instead, as DailyBriefPage
              does for the same reason.
            */}
            {/* flex-wrap: on a phone "Add destination" drops under the Type picker. A row that
                cannot wrap is as wide as its content, and pushed the whole composer sideways. */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-ink-label" aria-hidden>
                Type
              </span>
              <Select
                value={channel}
                // A thread cannot become another type: it is cut back to one post first, and once
                // part of it is live not even that. The reason is said under this row.
                disabled={readOnly || threadSwitch.type != null}
                onChange={(e) => {
                  const next = e.target.value as ContentChannel;
                  setChannel(next);
                  // A social post always has a text box - there is no "Add text" to bring one
                  // back - so a draft that held only media gets one as it becomes Social.
                  if (next === ContentChannel.SOCIAL) {
                    const spare = newTextBlock();
                    setBlocks((prev) => ensureSocialText(prev, spare));
                  }
                }}
                className="w-40"
                aria-label="Post type"
              >
                {CHANNEL_TAB_ORDER.map((c) => (
                  <option key={c} value={c}>
                    {enumLabel(c)}
                  </option>
                ))}
                {/* Only for a post that already IS one — see RETIRED_CHANNELS. Disabled, so it
                    labels the post without offering it to anything else. */}
                {RETIRED_CHANNELS.includes(channel) && (
                  <option value={channel} disabled>
                    {enumLabel(channel)}
                  </option>
                )}
              </Select>

              {/* Destinations sit WITH the type, because they are the same decision: what this post
                  is and where it goes. They used to float in the middle of this row, or on a line
                  of their own above the tabs. */}
              {channel === ContentChannel.SOCIAL && !readOnly && (
                <DestinationTabs
                  part="actions"
                  postId={effectiveId ?? null}
                  groupId={existing.data?.postGroupId ?? null}
                  currentConnectionId={connectionId}
                  currentPlatform={platform}
                  onSelect={(siblingId) =>
                    onSelectSibling ? onSelectSibling(siblingId) : navigate(`/posts?compose=${siblingId}`)
                  }
                  ensurePostId={ensurePostId}
                  addDisabledReason={addDestinationBlockedReason}
                />
              )}
            </div>

        {/* A brand-new post has no Schedule tab (tabs are edit-only), so a one-shot
            create-and-publish (NONE mode, releasable) keeps its Publish-now here. Editing a post
            publishes from the Schedule tab instead. It is all this row's right-hand end holds:
            Cancel and Save, which sat beside it, are in the bar along the bottom with "Save and
            close" (owner's ruling, 2026-10-20) - so on any other post nothing is drawn here, and
            the row leaves no empty space behind. */}
        {!isEditing && releasable && channel === ContentChannel.SOCIAL && (
          <Button
            loading={publish.isPending}
            disabled={publishNowHeld}
            onClick={async () => {
              setActionError(null);
              const dest = selectedAccount
                ? `${platformName(selectedAccount.platform)} · ${selectedAccount.accountName}`
                : 'the selected account';
              if (
                await confirm({
                  title: 'Publish now',
                  // With every post of a thread already live, nothing goes out - and it says so.
                  message: nothingToSend
                    ? threadFinishConfirmText(destinationPlatform)
                    : `Publish this post to ${dest} now? It posts to your live account immediately.`,
                  confirmLabel: 'Publish',
                })
              )
                publish.mutate();
            }}
          >
            Publish now
          </Button>
        )}
      </div>

      {/* Why Type cannot change while the post is a thread - under the row that holds it. */}
      {threadSwitch.type && <p className="text-xs text-ink-muted">{threadSwitch.type}</p>}

      {/*
        Video is pickable and a video post saves like any other, but nothing delivers one yet —
        publishing is an n8n handoff no video destination is wired to. Said HERE, directly under the
        control that chose it, so it is read when the type is picked rather than discovered after a
        post has been written, scheduled and left waiting for a release that isn't coming.
      */}
      {channel === ContentChannel.VIDEO && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint px-3 py-2 text-sm text-amber-800 dark:text-warning-dark">
          <Construction className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            This feature is currently under construction. We will be adding this functionality soon
            to allow posting to YouTube, TikTok, and Vimeo.
          </span>
        </div>
      )}

      {readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint px-3 py-2 text-sm text-amber-800 dark:text-warning-dark">
          <Lock className="h-4 w-4 shrink-0" aria-hidden />
          {approvalType === ApprovalType.POST ? (
            <>
              <span>
                This post is {gateState ? enumLabel(gateState).toLowerCase() : 'locked'}.
              </span>
              {/*
                Approval used to be a one-way door: a final approval auto-schedules the post and no
                endpoint returned it to DRAFT, so "request changes on it" was the only way back and
                required a reviewer. Plans change at an hour's notice; this is the author's own way
                back. It unschedules — a reopened post holding a past time would file itself under
                Failed while it was being rewritten.
              */}
              {!existing.data?.publishedAt && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-auto"
                  loading={reopen.isPending}
                  onClick={() => void askReopen()}
                >
                  Reopen for editing
                </Button>
              )}
            </>
          ) : existing.data?.publishedAt ? (
            <span>This post has been published.</span>
          ) : approvalType === ApprovalType.CAMPAIGN && ownStateReopenable ? (
            <>
              <span>
                {existing.data?.publishError
                  ? 'This post failed to publish. You can choose another account below and retry it as it is. To edit its text, reopen it — it will need approving again before it publishes.'
                  : `This post is ${gateState ? enumLabel(gateState).toLowerCase() : 'approved'}. To edit it, reopen it — it will need approving again before it publishes.`}
              </span>
              {/* Just this post goes back to Draft; the rest of its lane - often already published -
                  is left alone. */}
              <Button
                variant="secondary"
                size="sm"
                className="ml-auto shrink-0"
                loading={reopen.isPending}
                onClick={() => void askReopen()}
              >
                Reopen this post
              </Button>
            </>
          ) : approvalType === ApprovalType.CAMPAIGN && ownState != null ? (
            <span>
              This post is {enumLabel(ownState).toLowerCase()} — withdraw its lane from review on the
              campaign page to edit it.
            </span>
          ) : (
            <>
              This post&apos;s campaign is{' '}
              {campaignState ? enumLabel(campaignState).toLowerCase() : 'locked'} — reopen the
              campaign (request changes) to edit its posts.
            </>
          )}
        </div>
      )}

      {/* How the published post is doing on its platform: this destination's own numbers. */}
      {existing.data?.publishedAt && <PostPerformance post={existing.data} />}

      {/* items-start: each column takes its natural height. Stretching made the post card grow
          to match a viewport-tall side pane, leaving a half-empty white card beside it. */}
      {/* The row the Media Library pane sits in: the pane's height is measured from where this row
          starts (useComposerPaneHeight), and read by the pane as `--composer-pane-h`. */}
      <div ref={setPaneRow} className="grid items-start gap-4 lg:grid-cols-3">
        {/* min-w-0: a grid item is never narrower than its content unless told it may be. The
            account picker's label does not wrap, so a long account name made this card - and with
            it the whole composer - wider than a phone's screen (measured: 35px at 390px wide). */}
        <Card className="min-w-0 lg:col-span-2">
          <CardBody className="space-y-4">
            {/* Campaign */}
            {campaignLocked ? (
              <FormField label="Campaign" htmlFor={campaignFieldId}>
                <div
                  id={campaignFieldId}
                  className="flex items-center gap-2 rounded-lg border border-line bg-page px-3 py-2 text-sm text-ink-strong"
                >
                  <Lock className="h-3.5 w-3.5 text-ink-faint" aria-hidden />
                  {selectedCampaign?.name ?? existing.data?.campaignName ?? 'This campaign'}
                </div>
              </FormField>
            ) : (
              <FormField label="Campaign" htmlFor={campaignFieldId} required>
                {/* The two ESCAPES from the list are buttons, not rows in it. As options they sat
                    below every campaign the workspace has ever had — findable only by scrolling
                    past the thing you were searching — and "new campaign" is not a campaign. */}
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    id={campaignFieldId}
                    value={campaignId}
                    disabled={creatingNewCampaign || oneOff}
                    onChange={(e) => {
                      setOneOff(false);
                      setCreatingNewCampaign(false);
                      setCampaignId(e.target.value);
                    }}
                    // Workspaces accumulate campaigns; by the time there are thirty, scrolling is
                    // not how anyone finds the one they mean.
                    searchable
                    searchPlaceholder="Search campaigns…"
                    placeholder="Select a campaign"
                    className="w-full max-w-md sm:w-72"
                  >
                    {campaigns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                  <Button
                    type="button"
                    size="sm"
                    variant={creatingNewCampaign ? 'primary' : 'secondary'}
                    aria-pressed={creatingNewCampaign}
                    onClick={() => {
                      setOneOff(false);
                      // A second press goes back to the picker rather than stranding them in a
                      // mode with no way out but reopening the modal.
                      setCreatingNewCampaign((on) => !on);
                    }}
                  >
                    ➕ New campaign
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={oneOff ? 'primary' : 'secondary'}
                    aria-pressed={oneOff}
                    onClick={() => {
                      setCreatingNewCampaign(false);
                      setOneOff((on) => !on);
                    }}
                  >
                    🗒️ No campaign
                  </Button>
                </div>
                {creatingNewCampaign && (
                  <div className="mt-2">
                    <Input
                      id={newCampaignFieldId}
                      value={newCampaignName}
                      onChange={(e) => setNewCampaignName(e.target.value)}
                      placeholder="New campaign name"
                      className="max-w-md"
                      aria-label="New campaign name"
                    />
                  </div>
                )}
                {oneOffBlocked ? (
                  <p className="mt-2 rounded-lg border border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint px-3 py-2 text-sm text-amber-800 dark:text-warning-dark">
                    One-off posts aren’t allowed while approvals are set at the <strong>Campaign</strong>{' '}
                    level — choose or create a campaign, or switch approvals to Per-Post in Settings.
                  </p>
                ) : (
                  oneOff && (
                    <p className="mt-2 text-xs text-ink-muted">
                      This post will be grouped under the “One-Off Posts” campaign.
                    </p>
                  )
                )}
              </FormField>
            )}

            {/* Under Campaign in both of its forms: the locked box (every edit, and a new post
                opened from a campaign) and the picker. Which posting schedule the post is queued
                into: General or a category. Changed on a draft only, like the title. */}
            <SchedulingCategoryField
              value={categoryFieldValue}
              onChange={setCategoryId}
              disabled={readOnly}
            />

            {/* A social post's title is never published — the server no longer sends it in place
                of a text the post does not have. It is how the post is found in the pipeline, the
                calendar and the approval queues. "Hook / opening line" read like the first line of
                the post itself, which is what the Content pieces below are for. */}
            <FormField
              label="Title"
              htmlFor={titleId}
              hint={
                channel === ContentChannel.SOCIAL
                  ? 'Only your team sees the title. It is never published.'
                  : undefined
              }
            >
              <Input
                id={titleId}
                value={title}
                disabled={readOnly}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  channel === ContentChannel.BLOG
                    ? 'Blog headline'
                    : channel === ContentChannel.SOCIAL
                      ? 'Internal title'
                      : 'Hook / opening line'
                }
              />
            </FormField>

            {channel === ContentChannel.SOCIAL ? (
              <FormField label="Platform" htmlFor={platformFieldId}>
                {connectionsQ.isLoading ? (
                  <p className="text-sm text-ink-faint">Loading your connected accounts…</p>
                ) : accounts.length === 0 ? (
                  <p className="text-sm text-ink-muted">
                    No connected accounts yet.{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/connections')}
                      className="font-medium text-link hover:text-action-hover"
                    >
                      Connect one
                    </button>{' '}
                    to choose where to publish.
                  </p>
                ) : (
                  <Select
                    id={platformFieldId}
                    // The reconnect note below is read out with the picker.
                    aria-describedby={reconnectAccount ? reconnectNoteId : undefined}
                    // Drive off the RESOLVED account, not the raw id: a stale id (its account was
                    // disconnected) matches no option and would otherwise display the first one.
                    value={selectedAccount?.id ?? ''}
                    // Stay enabled when the post is releasable (approved) even though it's read-only,
                    // so you can pick/repick a connected account to publish to (avoids a dead-end
                    // when the post's original account was disconnected).
                    disabled={(readOnly && !releasable) || setAccount.isPending}
                    onChange={(e) => {
                      const chosen = e.target.value;
                      setConnectionId(chosen || null);
                      const acct = accounts.find((a) => a.id === chosen);
                      if (acct) setPlatform(acct.platform);
                      // A locked post has no Save: its choice is saved at once, so "Retry" and the
                      // scheduled release publish to it too. So is the choice for a thread part of
                      // which is already live: it has to stay on the account it went out from, and
                      // the server's answer to that arrives now - a refusal puts the picker back.
                      if ((readOnly || hasLockedPost) && savedPostId && chosen) {
                        setAccount.mutate(chosen);
                      }
                    }}
                    className="max-w-xs"
                  >
                    {/* An unset connectionId renders as value="" which matches NO option, and a
                        browser then DISPLAYS THE FIRST one — so the user sees an account that the
                        form doesn't actually have, and Publish now blocks for a reason the UI
                        appears to contradict. Keep an explicit placeholder until one is chosen. */}
                    {!selectedAccount && (
                      <option value="">
                        {connectionId
                          ? 'Previous account unavailable — choose one…'
                          : existing.data?.socialAccountName
                            ? `Was ${existing.data.socialAccountName} — choose an account…`
                            : 'Choose an account…'}
                      </option>
                    )}
                    {accounts.map((a) => (
                      <option
                        key={a.id}
                        value={a.id}
                        // A thread only goes to X or Threads, and once part of it is live it stays
                        // on its own platform: the other accounts are shown, and cannot be chosen.
                        disabled={
                          !threadAccountAllowed(blocks, a.platform, existing.data?.platform ?? null)
                        }
                      >
                        {/* aria-hidden: PlatformIcon labels itself, and the row already says the
                            platform's name — without this every option announces it twice. */}
                        <span className="inline-flex items-center gap-2" aria-hidden>
                          <PlatformIcon platform={a.platform} size="sm" />
                          <span>
                            {platformName(a.platform)} · {a.accountName}
                            {a.needsReconnect ? ' (reconnect needed)' : ''}
                          </span>
                        </span>
                      </option>
                    ))}
                  </Select>
                )}
                {/* Amber, first under the picker: the chosen account's sign-in has to be renewed on
                    Connections before anything publishes through it. In a new tab, so the post
                    being written here stays open; back on this tab the list is read again. */}
                {reconnectAccount && (
                  <p id={reconnectNoteId} className="mt-1.5 text-xs text-amber-600 dark:text-warning-dark">
                    Reconnect {reconnectAccount.accountName} by going to{' '}
                    <a
                      href={`/connections?ws=${encodeURIComponent(tenantKey)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      // Underlined at rest, as the link in the test-mode warning is: in the dark
                      // theme its grey and the note's amber are too close in lightness to tell apart.
                      className="font-medium text-link underline underline-offset-2 hover:text-action-hover"
                    >
                      Connections<span className="sr-only"> (opens in a new tab)</span>
                    </a>{' '}
                    and clicking the reconnect button on the {reconnectAccount.accountName} section.
                    You will be prompted to login with your {reconnectSignInName(reconnectAccount)}{' '}
                    credentials and reauthenticate.
                  </p>
                )}
                {/* Under the picker, while the post is a thread: why some accounts above cannot
                    be chosen. */}
                {threadSwitch.account && !connectionsQ.isLoading && accounts.length > 0 && (
                  <p className="mt-1.5 text-xs text-ink-muted">{threadSwitch.account}</p>
                )}
                {/* Amber: the chosen account cannot publish a thread (Threads, without the
                    permission for it). The post still saves; it cannot be scheduled or sent. */}
                {gates.permissionHold && (
                  <p className="mt-1.5 text-xs text-amber-600 dark:text-warning-dark">{gates.permissionHold}</p>
                )}
                {selectedAccount &&
                  IMAGE_PUBLISH_PLATFORMS.has(selectedAccount.platform) &&
                  !hasImage && (
                    <p className="mt-1.5 text-xs text-amber-600 dark:text-warning-dark">
                      {platformName(selectedAccount.platform)} needs an image — add one to publish
                      (you can still save this post).
                    </p>
                  )}
                {selectedAccount &&
                  !canPublish &&
                  !IMAGE_PUBLISH_PLATFORMS.has(selectedAccount.platform) && (
                    <p className="mt-1.5 text-xs text-amber-600 dark:text-warning-dark">
                      Publishing to {platformName(selectedAccount.platform)} isn&apos;t supported
                      yet (it needs an image or video) — you can still save this post.
                    </p>
                  )}
              </FormField>
            ) : (
              <FormField label="Destination" htmlFor={platformFieldId}>
                <Select
                  id={platformFieldId}
                  value={platformLabel}
                  disabled={readOnly}
                  onChange={(e) => setPlatformLabel(e.target.value)}
                  className="max-w-xs"
                >
                  {nonSocialOptions(channel).map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}

            {/*
              Export is the one destination that produces a FILE, so it is the only one with a format
              — and the only one whose action is Download rather than publish. A Select rather than
              radios because this app has no radio primitive anywhere; the one other "pick a format
              then download" control (the design bundle) uses a pressed-button pair, which is heavier
              than two options need.
            */}
            {isExportBlog && (
              <FormField label="File format" htmlFor={exportFormatFieldId}>
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    id={exportFormatFieldId}
                    value={exportFormat}
                    disabled={readOnly}
                    onChange={(e) => setExportFormat(e.target.value as BlogExportFormat)}
                    className="max-w-xs"
                  >
                    {BLOG_EXPORT_FORMATS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </Select>
                  <Button
                    variant="secondary"
                    loading={downloading}
                    disabled={!savedPostId}
                    title={
                      savedPostId ? undefined : 'Save this post first — the file is built from what is saved.'
                    }
                    onClick={() => void downloadExport()}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    Download
                  </Button>
                </div>
                {/* What each format actually gives you, said here rather than discovered after
                    downloading. Markdown alone cannot carry image bytes, and a link into private
                    storage would be dead the moment the file left the browser. */}
                <p className="mt-1.5 text-xs text-ink-muted">
                  {exportFormat === 'markdown'
                    ? 'Markdown exports the text as written. Choose Word doc or HTML to include images.'
                    : exportFormat === 'docx'
                      ? 'Formatting comes through as Word styles — headings, lists, tables and links — with images embedded.'
                      : 'A single self-contained HTML5 page: semantic headings, lists and tables, with images embedded in the file.'}
                </p>
              </FormField>
            )}

            {/* Per-platform scheduling: changing where this post goes costs it its slot. Said under
                the field that causes it and BEFORE the save that applies it. Deliberately OUTSIDE
                the channel branch above — moving a scheduled social post to Blog/Video/Podcast
                drops the slot too, and that switch hides the Platform field, so a warning nested
                inside it would unmount at exactly the moment it's needed. */}
            {platformChangeDropsSchedule && (
              <div className="max-w-md space-y-0.5 text-sm text-amber-600 dark:text-warning-dark">
                <p>
                  Changing the {channel === ContentChannel.SOCIAL ? 'platform' : 'content type'} of a
                  scheduled post requires you to reschedule the post.
                </p>
                <p>
                  If you save the post, your post date of {formatDateTimeLong(savedScheduledAt)} will
                  be removed, and you will need to reschedule.
                </p>
              </div>
            )}

            {/* The text was written for a different platform, which likely has a different length
                limit and conventions. Sits under the Platform field, with the schedule warning
                above it when a platform change costs both — the lost slot is the bigger
                consequence, so it reads first. */}
            {showRewriteNotice && (
              <div className="flex max-w-md items-start gap-2 text-sm text-amber-600 dark:text-warning-dark">
                {/* pt-1: level with the middle of the button beside it. */}
                <p className="flex-1 pt-1">
                  You have changed posting platforms. Please rewrite your text to fit the new
                  platform&apos;s character limit.
                </p>
                <IconButton label="Dismiss" onClick={dismissRewrite}>
                  <X className="h-4 w-4" aria-hidden />
                </IconButton>
              </div>
            )}

            {/* A thread stopped on a post nobody could confirm went out. Until someone says which,
                nothing more of it is sent - Schedule, the queue and Publish are held with this same
                sentence as the reason (gates.unconfirmedHold). */}
            {existing.data?.thread?.unconfirmed && <ThreadUnconfirmedNotice post={existing.data} />}

            <div>
              {/* The thread an AI conversion made, in review: at the top of the content section,
                  above "Content" (spec section 4) - Undo, Regenerate, Show changes, dismiss. */}
              {conversion.banner && (
                <div className="mb-3">
                  <ThreadConvertBanner
                    {...conversion.banner}
                    // Undo and Regenerate change the thread's shape: they wait while a save, a
                    // publish or an AI call is away, as its add, move and delete do - and while a
                    // file is on its way into a post. Landed on posts 2..N it makes them ask first
                    // (D17); still on its way, it would be thrown away without a word.
                    busy={editorBusy || uploading}
                    onShowChanges={conversion.setShowChanges}
                    onUndo={() => void conversion.undo()}
                    onRegenerate={() => void conversion.regenerate()}
                    onDismiss={() => {
                      conversion.dismiss();
                      contentLabelRef.current?.focus();
                    }}
                  />
                </div>
              )}
              <p
                ref={contentLabelRef}
                // Focusable from code only, and only where the banner can be: the label of a blog
                // post's content stays the bare paragraph it always was.
                tabIndex={channel === ContentChannel.SOCIAL ? -1 : undefined}
                className="mb-1.5 text-sm font-medium text-ink-strong"
              >
                Content
                {/* Beside the label, while the post is a thread: how many posts, and how many are
                    already live. The region is there on every social post, so that starting a
                    thread - and each post added or deleted after - is read out. */}
                {channel === ContentChannel.SOCIAL && (
                  <span aria-live="polite" className="ml-2 text-xs font-normal text-ink-muted">
                    {threadSummary}
                  </span>
                )}
              </p>
              {/* "Converting your text into a thread", said while an AI conversion is on its way
                  (spec section 2). Here on every social post, and empty but for that: a live region
                  is read out when its text changes, and Regenerate draws its progress card afresh,
                  so one inside the card would never be heard. The thread it brings is said by the
                  line beside "Content" as it appears. */}
              {channel === ContentChannel.SOCIAL && (
                <p className="sr-only" role="status" aria-live="polite">
                  {conversion.converting ? 'Converting your text into a thread' : ''}
                </p>
              )}
              {/* After "Save and return to post", what the person should know about what arrived. */}
              {mediaNotice && (
                <div className="mb-3 flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <AlertBanner tone="warning">{mediaNotice}</AlertBanner>
                  </div>
                  <Button size="sm" variant="ghost" className="min-h-11" onClick={() => setMediaNotice(null)}>
                    Dismiss
                  </Button>
                </div>
              )}
              <ContentBlocksEditor
                blocks={blocks}
                setBlocks={setEditorBlocks}
                charLimit={charLimit}
                imageLimit={imageLimit}
                platform={destinationPlatform}
                onRequestMediaLibrary={(anchorKey) => setMediaPicker({ anchorKey, mode: 'gallery' })}
                onRequestGenerateMedia={(anchorKey) => setGenerateFor({ kind: 'media', anchorKey })}
                onRequestDocumentLibrary={(anchorKey) =>
                  setMediaPicker({ anchorKey, mode: 'document' })
                }
                onUploadingChange={uploads.report}
                uploadsAt={uploads.counts}
                focusedKey={focusedKey}
                onFocusBlock={focusBlock}
                readOnly={readOnly}
                social={channel === ContentChannel.SOCIAL}
                // While a save, a publish or an AI call is away the shape of a thread holds still;
                // while it is being sent, its text and media too - a post typed into now could
                // come back live, and every save after that would be refused.
                busy={editorBusy}
                sending={publish.isPending}
                // The AI "Convert to thread" card under a single post's text: the suggestion, the
                // progress or the error. Null when there is none - the editor takes anything else
                // for a card, and hands focus back to the text box only when one comes or goes.
                afterText={
                  conversion.card ? (
                    <ThreadConvertCard
                      {...conversion.card}
                      // What Convert and Retry bring replaces the post's text: they wait while a
                      // save, a publish or an AI Assist draft is away (D12, the other way round).
                      busy={editorBusy}
                      onConvert={conversion.convert}
                      onCancel={conversion.cancel}
                      onRetry={conversion.retry}
                      onKeep={conversion.keepSingle}
                    />
                  ) : null
                }
                converting={conversion.converting}
                changesShown={conversion.changesShown}
                mediaToSet={
                  savedPostId && siblingCount > 0
                    ? {
                        onApply: (assetIds) => applyMediaToSet.mutate(assetIds),
                        pending: applyMediaToSet.isPending,
                        // It saves first, so it waits whenever Save does: while the post cannot
                        // be saved as it stands, and while another save, a publish or an AI draft
                        // is away.
                        held:
                          persistCount > 0 ||
                          publish.isPending ||
                          generate.isPending ||
                          saveBlocked,
                        // Why, when it is the form that holds it: the first of the lines beside
                        // Save, in the bar. Worked out on every draw, so it goes when its cause does
                        // - and no click that could only be refused leaves its reason on the error
                        // line.
                        heldReason: gates.uploadHold ?? gates.saveError ?? gates.saveHint,
                        notice: mediaSetNotice,
                      }
                    : undefined
                }
              />
            </div>

            {actionError && <p className="text-sm text-red-600 dark:text-error-dark">{actionError}</p>}
          </CardBody>
        </Card>

        {mediaPicker?.mode === 'document' && (
          <MediaLibraryPanel
            key={`document:${mediaPicker.anchorKey}`}
            // One PDF for the PDF panel that asked; LinkedIn takes one per post.
            single
            remaining={1}
            accept={isDocumentMime}
            acceptLabel="PDF"
            emptyTitle="No PDFs in your library"
            insertLabel="Use this PDF"
            onInsert={(assets) =>
              setBlocks((prev) => attachDocument(prev, mediaPicker.anchorKey, assets[0]))
            }
            onClose={() => setMediaPicker(null)}
          />
        )}
        {mediaPicker?.mode === 'gallery' && (
          <MediaLibraryPanel
            // Keyed by the requesting gallery: opening the panel from a DIFFERENT gallery must
            // start a fresh selection, not carry picks over to somewhere they weren't chosen for.
            key={mediaPicker.anchorKey}
            remaining={mediaSlot.remaining}
            held={channel === ContentChannel.SOCIAL ? mediaSlot.held : undefined}
            // While a thread is being sent nothing can enter its posts (setEditorBlocks), so Insert
            // is held rather than closing the panel with nothing inserted.
            insertDisabled={publish.isPending && isThreadForm(blocks)}
            onInsert={(assets) =>
              setEditorBlocks((prev) =>
                // The picker already refuses a mixed selection; this is the backstop against the
                // post changing underneath it. In a thread the files go to the post that holds
                // the gallery, under its cap and its own rule against photos beside video - and
                // nowhere while the thread is being sent (setEditorBlocks).
                insertMediaAtAnchor(prev, mediaPicker.anchorKey, assets, imageLimit, {
                  social: channel === ContentChannel.SOCIAL,
                  recheckMixing: true,
                }),
              )
            }
            onClose={() => setMediaPicker(null)}
          />
        )}
        {/* Hidden, not unmounted, while the Media Library has its place: the open tab and anything
            in it are still there when the library closes. min-w-0 lets this grid column be
            narrower than its content, so the card can never push the composer past a phone's
            screen. */}
        <div hidden={mediaPicker != null} className="min-w-0">
          <PreviewAssistCard
            preview={previewPanel}
            assist={
              <div className="space-y-2">
                <p className="text-sm text-ink-muted">Azure OpenAI · brand voice injected</p>
                {readOnly ? (
                  <p className="text-sm text-ink-muted">
                    {existing.data?.publishedAt
                      ? 'This post has been published.'
                      : approvalType === ApprovalType.POST || ownState != null
                        ? 'Editing is locked while this post is under review or beyond.'
                        : 'Editing is locked while the campaign is under review or beyond.'}
                  </p>
                ) : (
                  <>
                    <Button
                      className="w-full"
                      loading={generate.isPending && generate.variables === 'full'}
                      disabled={busy}
                      onClick={() => {
                        setActionError(null);
                        generate.mutate('full');
                      }}
                    >
                      Generate full draft
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-full"
                      loading={generate.isPending && generate.variables === 'outline'}
                      disabled={busy}
                      onClick={() => {
                        setActionError(null);
                        generate.mutate('outline');
                      }}
                    >
                      Generate outline
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-full"
                      loading={generate.isPending && generate.variables === 'expand'}
                      disabled={busy}
                      onClick={() => {
                        setActionError(null);
                        generate.mutate('expand');
                      }}
                    >
                      Expand current text
                    </Button>
                    <p className="pt-1 text-xs text-ink-faint">
                      Applies to the selected text piece — using the title as the prompt and that
                      piece&apos;s text as the outline.
                    </p>
                    {neverSayTerms.length > 0 && (
                      <p role="status" className="text-sm text-amber-700 dark:text-warning-dark">
                        {neverSayWarning(neverSayTerms, 'draft')}
                      </p>
                    )}
                  </>
                )}
              </div>
            }
          />
        </div>

        {/* Generate design — the push direction, next to AI assist because both are "do something
            with this post". Saved posts only: the design is built from persisted content, so an
            unsaved draft would produce an empty artboard. */}
        {isEditing && id && (
          <Card className="mt-4">
            <CardHeader
              title="Generate design"
              description="Build a design from this post, ready to open in Figma or Canva"
            />
            <CardBody>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => setGenerateFor({ kind: 'design', anchorKey: null })}
              >
                <Shapes className="h-4 w-4" aria-hidden />
                Generate design
              </Button>
              <p className="pt-2 text-xs text-ink-faint">
                One artboard at the size you choose, built on a workspace template or your brand
                assets — editable, not flattened.
              </p>
            </CardBody>
          </Card>
        )}
      </div>
        </div>
      )}

      {/* SCHEDULE tab — the AI-assisted "When to post" panel. */}
      {activeTab === 'schedule' &&
        (!savedPostId ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-page px-4 py-3 text-sm text-ink-label">
            <span>Save this post first — then you can schedule it here.</span>
            <div>
              <Button
                loading={saveInPlace.isPending}
                disabled={saveBlocked}
                onClick={() => {
                  setActionError(null);
                  saveInPlace.mutate();
                }}
              >
                Save post
              </Button>
              {/* Neutral: nothing is wrong - a file is still on its way into the post. */}
              {gates.uploadHold && (
                <p className="mt-1.5 max-w-xs text-sm text-ink-muted">{gates.uploadHold}</p>
              )}
              {gates.saveError && (
                <p className="mt-1.5 max-w-xs text-sm text-red-600 dark:text-error-dark">{gates.saveError}</p>
              )}
              {/* Neutral: nothing is wrong yet - a post of the thread is still empty. */}
              {gates.saveHint && (
                <p className="mt-1.5 max-w-xs text-sm text-ink-muted">{gates.saveHint}</p>
              )}
              {/* Amber, and it does not disable Save: this one blocks PUBLISHING only. A post that
                  already breaks the rule has to stay saveable or it could never be fixed. */}
              {gates.publishWarning && (
                <p className="mt-1.5 max-w-xs text-sm text-amber-700 dark:text-warning-dark">
                  {gates.publishWarning} It will save, but can&rsquo;t publish until that&rsquo;s fixed.
                </p>
              )}
            </div>
          </div>
        ) : (
        // Named and focusable from script only, for the focus that the save-first strip's Save leaves
        // behind when Next Available cannot take it (see schedulePanelRef).
        <div
          ref={schedulePanelRef}
          role="region"
          aria-label="Schedule"
          tabIndex={-1}
          className="space-y-4 focus:outline-none"
        >
          {/*
            An export post has nowhere to publish TO, so every scheduling control here plans a
            content calendar rather than arranging a release. Said up front, because the tab is
            otherwise identical to a social post's and picking a time looks like it commits to
            something.
          */}
          {isExportBlog && (
            <div className="rounded-xl border border-line bg-tint p-4 text-sm text-ink">
              This post has no live-publishing integration. Use this scheduler to plan your content
              calendar — selecting a time here does not publish anything automatically.
            </div>
          )}
          {/*
            An export post never reaches Published on its own — there is no send to complete — so the
            only way out of "Ready to schedule" is someone saying it went live and where. Offered
            once it is approved, because marking an unapproved post published would skip the review
            it was waiting for.
          */}
          {isExportBlog && !existing.data?.publishedAt && releasable && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-card p-4 text-sm shadow-card">
              <span className="text-ink-label">
                Published this on your platform? Record where it went live.
              </span>
              <Button onClick={() => setMarkingPublished(true)}>Mark as published</Button>
            </div>
          )}
          {isExportBlog && existing.data?.liveUrl && (
            <p className="text-sm text-ink-label">
              Live at{' '}
              <a
                href={existing.data.liveUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="focus-ring font-medium text-link hover:underline"
              >
                {existing.data.liveUrl}
              </a>
            </p>
          )}
          <div className="rounded-xl border border-line bg-card p-4 text-sm shadow-card">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span>
            <span className="text-ink-muted">Status: </span>
            <span className="font-medium text-ink">
              {existing.data?.publishedAt
                ? `Published ${formatRelative(existing.data.publishedAt)}`
                : existing.data?.scheduledAt
                  ? `${existing.data.queued ? 'Queued' : 'Scheduled'} for ${formatDateTime(existing.data.scheduledAt)}`
                  : // "Post as soon as approved" IS a release choice, just not a dated one. Without
                    // this the status stayed on "Not scheduled" after choosing it, so the option
                    // looked like it did nothing — the flag was saved, it simply wasn't reported.
                    existing.data?.publishWhenApproved
                    ? 'Post as soon as approved'
                    : // A platform change dropped this post's slot — say so instead of the flat "Not
                      // scheduled", which reads like the user never picked a time.
                      needsReschedule
                      ? 'Reschedule required'
                      : 'Not scheduled'}
            </span>
            </span>

            {/*
              Offer the decision you just made to the rest of the set.
              
              Only after a choice, and only while it still stands: the button asks about THIS
              decision, so it goes away once taken or superseded. A set of one has nothing to apply
              to, which is why it is gated on a real sibling rather than on merely having a group.
            */}
            {siblingCount > 0 && setDecision && !setResult && (
              <Button
                variant="secondary"
                size="sm"
                loading={applyToSet.isPending}
                onClick={() => {
                  setActionError(null);
                  applyToSet.mutate(setDecision);
                }}
              >
                Apply Same Settings to Set?
              </Button>
            )}
            {setResult && <SetApplyResult result={setResult} />}
            </div>
            {needsReschedule && (
              <p className="mt-1.5 text-sm text-amber-600 dark:text-warning-dark">
                Changing the{' '}
                {existing.data?.channel === ContentChannel.SOCIAL ? 'platform' : 'content type'} of a
                scheduled post requires you to reschedule the post. Your post date of{' '}
                {formatDateTimeLong(rescheduleRequiredFrom)} has been removed. Please reschedule.
              </p>
            )}
          </div>

          {existing.data?.publishedAt ? (
            <div className="rounded-xl border border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint px-4 py-3 text-sm text-amber-800 dark:text-warning-dark">
              This post is already published.
            </div>
          ) : !canSchedule && !releasable ? (
            <div className="rounded-xl border border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint px-4 py-3 text-sm text-amber-800 dark:text-warning-dark">
              This post is under review — its schedule is locked until it’s approved or reopened
              (cancel review / request changes).
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_17rem]">
              <div className="space-y-4">
              <div className="overflow-hidden rounded-xl border border-line bg-card shadow-card">
                <p className="border-b border-line-soft px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                  When to post
                </p>
                <div className="divide-y divide-line-soft">
                  {/* Per-post approval only: submit needs a proposed time OR this. */}
                  {canSetPublishWhenApproved && (
                    <ScheduleOptionRow
                      icon={ShieldCheck}
                      title="Post as soon as approved"
                      description="No set time — it publishes the moment it’s approved."
                      // A stored flag, so unlike the queue rows this one still reads as chosen when
                      // the post is reopened — there is nothing to infer.
                      selected={existing.data?.publishWhenApproved ?? false}
                      // Turning it ON sets the post to go out, so it is held as Schedule is;
                      // turning it off always works.
                      disabled={
                        setPublishWhenApproved.isPending ||
                        busy ||
                        (publishBlocked && !(existing.data?.publishWhenApproved ?? false))
                      }
                      onClick={() => {
                        setActionError(null);
                        setWhenMode(null);
                        setPublishWhenApproved.mutate(!(existing.data?.publishWhenApproved ?? false));
                      }}
                    />
                  )}
                  {canSchedule && (
                    <>
                      {queueHeld ? (
                        // In place of the queue rows, which would place the post on the times of
                        // the category it is saved with, not the one the field shows. Set Date &
                        // Time below stays: an exact time is the same on any schedule.
                        <div className="flex flex-wrap items-center justify-between gap-3 bg-page/60 px-4 py-3 text-sm text-ink-label">
                          <span>
                            You changed the scheduling category. Save the post to schedule it with
                            that category&rsquo;s posting times.
                          </span>
                          <div>
                            <Button
                              loading={saveInPlace.isPending}
                              // Held when the Content tab's Save is, and while "Save and close" is away:
                              // the strip stays up through that save, and a second save beside it would
                              // write the post twice.
                              disabled={
                                publish.isPending ||
                                generate.isPending ||
                                applyMediaToSet.isPending ||
                                saveAndClose.isPending ||
                                oneOffBlocked ||
                                saveBlocked
                              }
                              onClick={() => {
                                setActionError(null);
                                setCategorySaving(true);
                                saveInPlace.mutate(undefined, {
                                  onSettled: () => setCategorySaving(false),
                                });
                              }}
                            >
                              Save post
                            </Button>
                            {/* Why Save is held, under it: the lines the Content tab's Save has
                                beside it, in the bar. */}
                            {/* Neutral: nothing is wrong - a file is still on its way into the post. */}
                            {gates.uploadHold && (
                              <p className="mt-1.5 max-w-xs text-sm text-ink-muted">{gates.uploadHold}</p>
                            )}
                            {gates.saveError && (
                              <p className="mt-1.5 max-w-xs text-sm text-red-600 dark:text-error-dark">
                                {gates.saveError}
                              </p>
                            )}
                            {/* Neutral: nothing is wrong yet - a post of the thread is still empty. */}
                            {gates.saveHint && (
                              <p className="mt-1.5 max-w-xs text-sm text-ink-muted">{gates.saveHint}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        scheduleEmpty &&
                        !suggestionsQ.isLoading && (
                          <p className="px-4 py-2.5 text-xs text-amber-600 dark:text-warning-dark">
                            <NoPostingTimes
                              categoryName={savedCategoryName}
                              offerCategory={(categoriesQ.data?.length ?? 0) > 0}
                              onOpenSettings={() => navigate('/posting-schedule')}
                            />{' '}
                            Set Date &amp; Time still works.
                          </p>
                        )
                      )}
                      {/* Next Available — append to the end of the queue. */}
                      {!queueHeld && (
                        <ScheduleOptionRow
                          icon={Clock}
                          title="Next Available"
                          description="Use the next open slot in your queue."
                          buttonRef={nextAvailableRef}
                          selected={chosenWhen === 'next'}
                          disabled={queueDisabled}
                          onClick={() => {
                            setActionError(null);
                            setWhenMode(null);
                            enqueue.mutate({ strategy: 'next' });
                          }}
                        />
                      )}
                      {/* Prioritize — jump to the front of the queue. */}
                      {!queueHeld && (
                        <ScheduleOptionRow
                          icon={Zap}
                          title="Prioritize"
                          description="Bump to the top of the queue."
                          selected={chosenWhen === 'prioritize'}
                          disabled={queueDisabled}
                          onClick={() => {
                            setActionError(null);
                            setWhenMode(null);
                            enqueue.mutate({ strategy: 'prioritize' });
                          }}
                        />
                      )}
                      {/* Set Date & Time — expand an exact datetime picker. */}
                      <ScheduleOptionRow
                        icon={CalendarClock}
                        title="Set Date & Time"
                        description="Choose a specific date and time."
                        chevron
                        selected={chosenWhen === 'date'}
                        active={whenMode === 'date'}
                        disabled={busy}
                        onClick={() => {
                          const opening = whenMode !== 'date';
                          setWhenMode(opening ? 'date' : null);
                          // Open on the next quarter hour rather than an empty field: `step` governs
                          // how the control MOVES, not where it starts. An existing time is left
                          // exactly as it is — including one off the quarter, which the queue can
                          // produce; showing 10:15 for a post scheduled at 10:07 would misreport it,
                          // and confirming would silently move it.
                          if (opening && !scheduleAt) setScheduleAt(nextQuarterHour(new Date()));
                        }}
                      />
                      {whenMode === 'date' && (
                        <div className="flex flex-wrap items-center gap-2 bg-page/60 px-4 py-3">
                          <input
                            type="datetime-local"
                            value={scheduleAt}
                            /**
                             * No past times. A time that has gone by cannot be honoured — the
                             * release worker only looks at posts due inside its grace window — so
                             * the post would sit there until it was late enough to read as an error.
                             *
                             * The floor is itself on a quarter hour, and that is load-bearing: a
                             * browser counts `step` FROM `min`, so a floor of 10:07 would make the
                             * valid minutes :07, :22, :37 and quietly undo the quarter-hour rule.
                             */
                            min={scheduleFloor}
                            // Quarter hours: the minute field moves :00 → :15 → :30 → :45. A posting
                            // time is a decision — "Tuesday at ten" — and a control offering 1,440
                            // minutes a day asks for a precision nobody means.
                            step={QUARTER_HOUR_STEP_SECONDS}
                            onChange={(e) => setScheduleAt(e.target.value)}
                            className="focus-ring rounded-lg border border-line-input bg-card px-3 py-2 text-sm text-ink"
                          />
                          <Button
                            loading={schedule.isPending}
                            disabled={!scheduleAt || pickedTimeIsPast || busy || publishBlocked}
                            onClick={() => {
                              setActionError(null);
                              schedule.mutate(new Date(scheduleAt).toISOString());
                            }}
                          >
                            {releasable ? 'Schedule' : 'Save proposed time'}
                          </Button>
                          {pickedTimeIsPast && (
                            <span className="text-xs text-amber-700 dark:text-warning-dark">
                              That time has already passed — pick a later one.
                            </span>
                          )}
                          {!releasable && (
                            <span className="text-xs text-ink-faint">
                              Proposed time — required before submitting for approval.
                            </span>
                          )}
                        </div>
                      )}
                      {/* Pick a Month — AI auto-places at the best open slot in that month. */}
                      {!queueHeld && (
                        <ScheduleOptionRow
                          icon={CalendarDays}
                          title="Pick a Month"
                          description="We’ll auto-place it at the best time."
                          chevron
                          selected={chosenWhen === 'month'}
                          active={whenMode === 'month'}
                          disabled={queueDisabled}
                          onClick={() => setWhenMode((m) => (m === 'month' ? null : 'month'))}
                        />
                      )}
                      {!queueHeld && whenMode === 'month' && (
                        <div className="flex flex-wrap items-center gap-2 bg-page/60 px-4 py-3">
                          <input
                            type="month"
                            value={monthValue}
                            min={monthBounds.min}
                            max={monthBounds.max}
                            onChange={(e) => setMonthValue(e.target.value)}
                            className="focus-ring rounded-lg border border-line-input bg-card px-3 py-2 text-sm text-ink"
                          />
                          <Button
                            loading={enqueue.isPending}
                            disabled={!monthValue || queueDisabled}
                            onClick={() => {
                              setActionError(null);
                              enqueue.mutate({ strategy: 'month', month: monthValue });
                            }}
                          >
                            Auto-place
                          </Button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Publish Now — only once approved/releasable. */}
                  {releasable &&
                    (channel === ContentChannel.SOCIAL ? (
                      <ScheduleOptionRow
                        icon={Send}
                        title="Publish Now"
                        description="Post immediately. Cannot be undone."
                        badge="NOW"
                        disabled={publishNowHeld}
                        onClick={async () => {
                          setActionError(null);
                          const dest = selectedAccount
                            ? `${platformName(selectedAccount.platform)} · ${selectedAccount.accountName}`
                            : 'the selected account';
                          if (
                            await confirm({
                              title: 'Publish now',
                              // With every post of a thread already live, nothing goes out - and
                              // it says so.
                              message: nothingToSend
                                ? threadFinishConfirmText(destinationPlatform)
                                : `Publish this post to ${dest} now? It posts to your live account immediately.`,
                              confirmLabel: 'Publish',
                            })
                          )
                            publish.mutate();
                        }}
                      />
                    ) : (
                      <ScheduleOptionRow
                        icon={Send}
                        title="Publish Now"
                        description="Post immediately. Cannot be undone."
                        badge="NOW"
                        disabled={busy || gates.publishWarning != null}
                        onClick={async () => {
                          setActionError(null);
                          if (
                            await confirm({
                              title: 'Publish now',
                              message: 'Publish this post now? This notifies your n8n workflow.',
                              confirmLabel: 'Publish',
                            })
                          )
                            publishNonSocial.mutate();
                        }}
                      />
                    ))}
                </div>
                {publishBlockedReason && (
                  <p className="border-t border-line-soft px-4 py-2 text-xs text-amber-600 dark:text-warning-dark">
                    {publishBlockedReason}
                  </p>
                )}
                {/* Test mode — auto-publishing off entirely — is announced by the banner at the
                    top of this modal, so it isn't repeated here. This one is different: the
                    workspace HAS opted in, and the worker skips it anyway. */}
                {autoPublishOverrideGap && (
                  <p className="border-t border-line-soft px-4 py-2 text-xs text-amber-600 dark:text-warning-dark">
                    Approvals are off install-wide, but this workspace is still set to{' '}
                    {enumLabel(activeTenant?.approvalType ?? ApprovalType.CAMPAIGN)} approvals, so
                    auto-publishing skips it. Set this workspace’s own approval mode to “No
                    approvals” under Edit Brand Workspace for scheduled posts to publish.
                  </p>
                )}
              </div>

              {/* SUGGESTED SLOTS — AI-ranked open times. Held with the queue rows: they are times of
                  the category the post is saved with (queueHeld). */}
              {canSchedule && !queueHeld && (
                <div className="overflow-hidden rounded-xl border border-line bg-card shadow-card">
                  <p className="flex items-center gap-1.5 border-b border-line-soft px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden /> Suggested slots
                  </p>
                  {suggestionsQ.isLoading ? (
                    <p className="px-4 py-3 text-sm text-ink-faint">Finding the best open times…</p>
                  ) : suggestionsQ.isError ? (
                    <p className="px-4 py-3 text-sm text-red-600 dark:text-error-dark">Could not load suggestions.</p>
                  ) : suggestionsQ.data?.scheduleEmpty ? (
                    <p className="px-4 py-3 text-sm text-ink-muted">
                      <NoPostingTimes
                        categoryName={savedCategoryName}
                        offerCategory={(categoriesQ.data?.length ?? 0) > 0}
                        onOpenSettings={() => navigate('/posting-schedule')}
                      />
                    </p>
                  ) : (suggestionsQ.data?.suggestions ?? []).length === 0 ? (
                    <p className="px-4 py-3 text-sm text-ink-faint">No open slots in the near future.</p>
                  ) : (
                    <div className="divide-y divide-line-soft">
                      {suggestionsQ.data!.suggestions.map((s) => {
                        /**
                         * The slot this post is actually on.
                         *
                         * Matched on the INSTANT rather than remembered in state, so it still reads
                         * as chosen after the modal is closed and reopened. `queued` rules out a
                         * queue placement that happened to land on the same minute — that came from
                         * a different row and belongs to it.
                         */
                        const slotSelected =
                          !existing.data?.queued &&
                          existing.data?.scheduledAt != null &&
                          new Date(existing.data.scheduledAt).getTime() === new Date(s.isoTime).getTime();
                        return (
                        <button
                          key={s.isoTime}
                          type="button"
                          aria-pressed={slotSelected}
                          disabled={busy || publishBlocked}
                          onClick={() => {
                            setActionError(null);
                            schedule.mutate(s.isoTime);
                          }}
                          className={cn(
                            'focus-ring flex w-full items-start gap-3 px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60',
                            slotSelected ? 'bg-action text-action-fg' : 'hover:bg-hover',
                          )}
                        >
                          <Clock
                            className={cn(
                              'mt-0.5 h-4 w-4 shrink-0',
                              slotSelected ? 'text-action-fg' : 'text-ink-faint',
                            )}
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1">
                            <span
                              className={cn(
                                'block text-sm font-medium',
                                slotSelected ? 'text-action-fg' : 'text-ink',
                              )}
                            >
                              {s.label}
                            </span>
                            {s.rationale && (
                              <span
                                className={cn(
                                  'block text-xs',
                                  slotSelected ? 'text-action-fg/90' : 'text-ink-muted',
                                )}
                              >
                                {s.rationale}
                              </span>
                            )}
                          </span>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              </div>

              {/* Right pane — a condensed calendar to manually pick a day (then a time). */}
              {canSchedule && (
                <div className="lg:sticky lg:top-2 lg:self-start">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                    Pick a day
                  </p>
                  <SchedulePickerCalendar
                    tenantKey={tenantKey}
                    selectedDay={scheduleAt ? scheduleAt.slice(0, 10) : null}
                    onPickDay={(dayIso) => {
                      // Keep the current time-of-day if one is set, else default to 9:00 AM, then
                      // open the Set Date & Time editor so the user can fine-tune + confirm.
                      const time = scheduleAt ? scheduleAt.slice(11, 16) : '09:00';
                      const picked = `${dayIso}T${time}`;
                      // Picking TODAY keeps the time of day, which may already have gone by — hand
                      // back the next quarter hour instead of a value the field would refuse.
                      setScheduleAt(isInPast(picked) ? nextQuarterHour(new Date()) : picked);
                      setWhenMode('date');
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {actionError && <p className="text-sm text-red-600 dark:text-error-dark">{actionError}</p>}
        </div>
        ))}

      {/* APPROVAL tab — per-post approval (POST mode), this post's history, and campaign info. */}
      {activeTab === 'approval' &&
        (existing.data ? (
        <PostApprovalPanel
          post={existing.data}
          tenantKey={tenantKey}
          approvalType={approvalType}
          campaign={selectedCampaign}
          // What holds Schedule and Publish holds Submit too.
          submitHold={publishHold}
        />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-page px-4 py-3 text-sm text-ink-label">
            <span>Save this post first — then you can manage its approval here.</span>
            <div>
              <Button
                loading={saveInPlace.isPending}
                disabled={saveBlocked}
                onClick={() => {
                  setActionError(null);
                  saveInPlace.mutate();
                }}
              >
                Save post
              </Button>
              {/* Neutral: nothing is wrong - a file is still on its way into the post. */}
              {gates.uploadHold && (
                <p className="mt-1.5 max-w-xs text-sm text-ink-muted">{gates.uploadHold}</p>
              )}
              {gates.saveError && (
                <p className="mt-1.5 max-w-xs text-sm text-red-600 dark:text-error-dark">{gates.saveError}</p>
              )}
              {/* Neutral: nothing is wrong yet - a post of the thread is still empty. */}
              {gates.saveHint && (
                <p className="mt-1.5 max-w-xs text-sm text-ink-muted">{gates.saveHint}</p>
              )}
              {/* Amber, and it does not disable Save: this one blocks PUBLISHING only. A post that
                  already breaks the rule has to stay saveable or it could never be fixed. */}
              {gates.publishWarning && (
                <p className="mt-1.5 max-w-xs text-sm text-amber-700 dark:text-warning-dark">
                  {gates.publishWarning} It will save, but can&rsquo;t publish until that&rsquo;s fixed.
                </p>
              )}
            </div>
          </div>
        ))}

      {/* Both read the post as the editor holds it, and "Save and return to post" puts the result
          back in it. Mounted only while open, so each opening starts from the post as it is then. */}
      {generateFor?.kind === 'media' && (
        <GenerateMediaModal
          open
          onClose={() => setGenerateFor(null)}
          tenantKey={tenantKey}
          postContext={postContext}
          onSaveToPost={saveToPost}
        />
      )}
      {generateFor?.kind === 'design' && (
        <GenerateDesignModal
          open
          onClose={() => setGenerateFor(null)}
          postContext={postContext}
          onSaveToPost={saveToPost}
        />
      )}

      {/*
        The way out, always in the same place.

        Sticky rather than parked at the bottom of a long form: the composer scrolls inside a fixed
        modal, so a footer in the flow would be below the fold exactly when someone is looking for
        it. The next-step button sits beside it so the path forward and the way out are one glance
        apart, never a hunt.

        On the Content tab of a post that can be edited it also holds Cancel and Save ("Save
        changes" / "Save post"), just left of "Save and close" (owner's ruling, 2026-10-20) - they
        used to sit at the top of the tab, beside Type - and, left of all three, why Save is held or
        what will stop the post from publishing. A read-only post has one way out, "Close", on every
        tab. In the order they are drawn, so Tab moves through them as they are seen.

        `data-composer-bar`: pinned, it is drawn OVER the bottom of what scrolls, so the Media
        Library pane is made shorter by this bar's height (useComposerPaneHeight) - else the pane's
        own footer sits under it. Measured as it is, so a reason that makes it taller is taken off
        too: the reasons are INSIDE the bar for that.
      */}
      <div
        data-composer-bar
        className="sticky bottom-0 -mx-6 mt-2 flex flex-wrap items-center justify-end gap-x-4 gap-y-2 border-t border-line-soft bg-card/95 px-6 py-3 backdrop-blur"
      >
        {/* The lines that sat under Save, with it. flex-1 takes what the buttons leave; the 16rem
            basis is the least it takes beside them, so on a narrow screen the buttons wrap below
            the lines rather than squeezing them into a column. */}
        {activeTab === 'content' &&
          !readOnly &&
          (gates.uploadHold || gates.saveError || gates.saveHint || gates.publishWarning) && (
            <div className="min-w-0 flex-1 basis-64 space-y-1">
              {/* Neutral: nothing is wrong - a file is still on its way into the post. */}
              {gates.uploadHold && <p className="text-sm text-ink-muted">{gates.uploadHold}</p>}
              {gates.saveError && (
                <p className="text-sm text-red-600 dark:text-error-dark">{gates.saveError}</p>
              )}
              {/* Neutral: nothing is wrong yet - a post of the thread is still empty. */}
              {gates.saveHint && <p className="text-sm text-ink-muted">{gates.saveHint}</p>}
              {/* Amber, and it does not disable Save: this one blocks PUBLISHING only. A post that
                  already breaks the rule has to stay saveable or it could never be fixed. */}
              {gates.publishWarning && (
                <p className="text-sm text-amber-700 dark:text-warning-dark">
                  {gates.publishWarning} It will save, but can&rsquo;t publish until that&rsquo;s fixed.
                </p>
              )}
            </div>
          )}
        {/* The buttons wrap as one group: on a phone they go under the lines together, never one
            left beside them. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {activeTab === 'content' && !readOnly && (
            <Button variant="secondary" onClick={closeOrNavigate}>
              Cancel
            </Button>
          )}
          {/* The primary button on every channel, for a new post and a saved one alike (owner's
              ruling, 2026-10-19); a social post's used to be secondary. Held while "Apply Same
              Media to Set?" is saving: two saves at once would each send the rows the other is
              sending. */}
          {activeTab === 'content' && !readOnly && (
            <Button
              loading={save.isPending}
              disabled={
                publish.isPending ||
                generate.isPending ||
                applyMediaToSet.isPending ||
                oneOffBlocked ||
                saveBlocked
              }
              onClick={() => {
                setActionError(null);
                save.mutate();
              }}
            >
              {savedPostId ? 'Save changes' : 'Save post'}
            </Button>
          )}
          {scheduled && activeTab === 'schedule' && (
            <Button variant="secondary" onClick={() => setActiveTab('approval')}>
              Next: Approval
            </Button>
          )}
          {!readOnly && (
            <Button
              loading={saveAndClose.isPending}
              disabled={
                save.isPending || publish.isPending || generate.isPending ||
                applyMediaToSet.isPending || oneOffBlocked || saveBlocked
              }
              onClick={() => {
                setActionError(null);
                // Carrying a category change: the Schedule tab keeps holding the queue and the Suggested
                // Slots until this save is in, as for the strip's own Save (categorySaving).
                if (categoryUnsaved) {
                  setCategorySaving(true);
                  saveAndClose.mutate(undefined, { onSettled: () => setCategorySaving(false) });
                } else {
                  saveAndClose.mutate();
                }
              }}
            >
              Save and close
            </Button>
          )}
          {readOnly && (
            <Button variant="secondary" onClick={closeOrNavigate}>
              Close
            </Button>
          )}
        </div>
      </div>

      <MarkPublishedModal
        post={markingPublished ? (existing.data ?? null) : null}
        onClose={() => setMarkingPublished(false)}
        onDone={() => {
          setMarkingPublished(false);
          // The post's status, its campaign's rollup and every list that shows either all move.
          void queryClient.invalidateQueries({ queryKey: ['post', tenantKey, savedPostId] });
          void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
          void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
          if (resolvedCampaignId)
            void queryClient.invalidateQueries({
              queryKey: ['campaign', tenantKey, resolvedCampaignId],
            });
        }}
      />
    </div>
  );
}
